import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// 개선 9건 차수2c(26-08-04) §5 — '새 계약으로 등록' 유도 동선(그 자리에서 이동).
import { useNavigate } from 'react-router-dom';
// 계약 사실화 1단계 정정7(26-07-29) 결함 ③ — 공통 '계약 형태' 한 단어 → 줄 사유 규칙은
// 정본(contractTypeToLineReason) 하나만 부른다. 이 화면이 들고 있던 미러 사본을 지웠다
// (아래 그 자리 주석 참조).
import {
  contractTypeToLineReason,
  CustomerSegment,
  // 차수2d — 서버 가드(409)와 같은 문장을 같은 출처에서. 두 벌이면 서버만 고쳐도
  // 이 사전 안내가 낡은 문장을 계속 보여 준다.
  INSTALLED_CONTRACT_LINE_ADD_MESSAGE,
  UserRole,
} from '@bhops/shared';
import type { ListModelsResponse } from '@bhops/shared';
import {
  customerMasterApi,
  type CreateCustomerMasterBody,
  type CreateCustomerMasterRobotLine,
  type CustomerMasterRow,
  type UpdateCustomerMasterBody,
} from '@/api/customer-master';
import { modelsApi } from '@/api/models';
import { extractApiError } from '@/api/client';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { EditableSelect } from '@/components/EditableSelect';
import { ModelPicker } from '@/components/pickers/ModelPicker';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
// 계약 사실화 1단계 정정4(26-07-29) — 줄별 '계약 형태' 칸의 라벨은 표기 정본에서
// 가져온다(자체 세트 금지). 아래 LINE_REASON_OPTIONS 주석 참조.
// 계약 사실화 1단계 정정5(26-07-29) — 안내문("줄별로 다릅니다 (파일럿 1 · 구독 2)")과
// 줄별 상속 표기도 정본의 짧은 표기를 쓴다. 서버는 값과 대수만 내려주고 사람이 읽는
// 문구는 화면이 만든다(서버가 번역하면 라벨 세트가 또 갈라진다 — 정본 파일 주석 참조).
import { CONTRACT_REASON_LABEL, CONTRACT_REASON_OPTION_LABEL } from '@/lib/contract-reason';
import { toYmdInput } from '@/lib/format-date';
import { formatKoreanPhone } from '@/lib/format-phone';
import { useAuthStore } from '@/stores/auth';

// docs/plan/customer-master-ux/plan.md §3 — 신규 영업 기회 / 고객사 마스터
// 등록 long modal. 8 섹션 (고객사 / 담당자 / 영업 / 계약 / 로봇 / 간택기 /
// 일정 / 첨부 — 첨부는 Phase 3).
//
// Phase 2 의 단순 규칙:
//   - 상호 + 영업 담당 + 로봇 1+ 만 필수
//   - 계약 정보는 한 필드라도 채우면 모두 같이 보냄 (server hasContractInput)
//   - dedupe 안 함 (Q2)
//   - 가맹수 / 매장 자동 생성 없음 (Q4)
//   - 운영 계약 확정 / 추가 예상 / POC 개월 없음 (Q3)

interface RobotRow {
  // 계약 사실화 1단계 정정(26-07-28) — 편집 중인 기존 계약 줄의 id(신규 행은 없음).
  // 저장 시 그대로 되돌려 보내 서버가 어느 줄을 고친 것인지 추측하지 않게 한다.
  // 종전엔 (모델, 순서)로만 대응돼 앞 행을 지우면 남은 값이 엉뚱한 줄에 붙었다.
  lineId?: string;
  modelCode: string;
  quantity: number;
  // 계약 사실화 1단계 정정(26-07-28) — 줄별 계약 형태 **원문**(DeploymentReason 7값).
  // '' = '공통값 따름'. 그 줄이 실제로 무엇으로 저장되는지는 planLineType 이 서버와
  // 같은 규칙으로 계산해 줄 옆에 적는다(정정5 26-07-29 — 종전엔 "(공통값: 렌탈)"
  // 하나뿐이라, 파일럿 계약에서 지키지 못할 약속을 했다).
  // 종전엔 '렌탈'/'판매' 2값만 담아, 파일럿·시연·행사·사내·금융리스 줄을 열어 저장하면
  // 접힌 값이 되돌아가 원본이 렌탈로 덮였다.
  reason: string;
  setting: string;
  grillPlate: string;
  specialParts: string;
  wireGrill: string;
  unitPriceKrw: string;
  listPriceKrw: string;
  // S1 Phase 5 (R2 #3) — PDF §3 도입 로봇 모델 row 의 per-line 필드.
  // S2 Phase 2.5 — + 희망 방향.
  // FB2 §4B — 희망 색상/불감지 free-text 제거(결정 G: DB 컬럼·서비스 보존, 전송만 중단).
  // 희망 방향은 유지하되 모델별 picklist 로 전환.
  storeId: string;
  desiredDirection: string;
}

/**
 * 줄별 **사양** 글자 칸 — 계약 줄(ContractLine)에만 저장되는 칸과 그 화면 이름.
 * 계약이 안 만들어지는 저장에서 이 칸이 채워져 있으면 저장 전에 막는다(handleSubmit).
 * 이름은 이 창의 칸 이름을 따른다('#n 로봇 특수부위' · '#n 로봇 그릴 종류' · '#n 로봇
 * Store ID'). 방향 칸은 모델마다 이름이 달라 차단 안내에서는 그 모델의 축 이름
 * (variantAxisLabel, 칸의 placeholder 와 같다)으로 부른다 — 아래 '희망 방향' 은 축 이름이 없을 때.
 */
const LINE_SPEC_LABELS = [
  ['setting', '세팅'],
  ['grillPlate', '불판'],
  ['specialParts', '특수부위'],
  ['wireGrill', '그릴 종류'],
  ['storeId', 'Store ID'],
  ['desiredDirection', '희망 방향'],
  // 줄별 금액도 계약 줄에만 산다(딜 견적은 모델·대수뿐). '0' 은 값 없음과 같다 — 도입료
  // 컬럼이 NOT NULL 이라 비움이 0 으로 저장되고, 되읽을 때도 빈 칸으로 보인다.
  ['unitPriceKrw', '구독료'],
  ['listPriceKrw', '판매가'],
] as const satisfies ReadonlyArray<readonly [keyof CreateCustomerMasterRobotLine, string]>;
/** 그 줄 칸에 실제 값이 들어 있는가(전송부가 trim 한 값 기준, 금액 '0' 은 값 없음). */
function lineFieldHasValue(v: string | null | undefined, isPrice: boolean): boolean {
  const t = (v ?? '').trim();
  return t !== '' && !(isPrice && /^0+$/.test(t));
}

function emptyRobotRow(): RobotRow {
  return {
    modelCode: 'GEN2',
    quantity: 1,
    // 계약 사실화 1단계(26-07-28) — 기본은 '공통값 상속'. 판매/렌탈을 여기서
    // 미리 정하지 않는다(추측하지 않고 공통값을 따르며, 그 사실을 화면에 쓴다).
    reason: '',
    setting: '',
    grillPlate: '',
    specialParts: '',
    wireGrill: '',
    unitPriceKrw: '',
    listPriceKrw: '',
    storeId: '',
    desiredDirection: '',
  };
}

interface FormState {
  // §1 고객사 정보
  displayName: string;
  legalName: string;
  segment: string;
  // round11 (요구 11) — 국가(2자리 코드). 지금까지 입력 UI 가 없어 해외 고객사는
  // API 로만 등록 가능했다. 국내/해외 탭이 이 값으로 갈린다.
  countryCode: string;
  // round11 (요구 10) — 쇼잉 여부. 다른 Boolean? 필드(보증보험)와 같은 O/X/미입력.
  isShowing: 'O' | 'X' | '';
  expectedStoreCount: string; // S1 Phase 5 — PDF §1 '매장 수'
  addressLine: string;
  addressMapUrl: string;
  notes: string;
  // §2 고객 담당자
  primaryContactName: string;
  primaryContactPosition: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  // §3 도입 로봇 모델 (rows)
  robots: RobotRow[];
  // §4 계약 정보 — 영업 담당 / 렌탈사 / 보증보험 포함 (PDF §4)
  salesOwnerCode: string;
  // 운영자 피드백 (round 3) — '렌탈사 개입' YN → '렌탈사' string.
  rentalCompany: string;
  hasInsurance: 'O' | 'X' | '';
  contractType: string;
  // round5 #2b① — contractNumber 제거(서버가 읽지 않던 죽은 필드; humanId 자동 발번).
  contractedAt: string;
  endDate: string;
  rentalTermMonths: string;
  depositKrw: string;
  // 개선 9건 차수2(26-08-04) §7 (결정 ④A) — 지원금·소개비(계약 단위). 보증금 옆 칸.
  subsidyKrw: string;
  referralFeeKrw: string;
  subscriptionStatus: string;
  // §6 기타 정보
  grillSelectorType: string;
  installScheduledAt: string;
}

// round11 (요구 11) — 등록 시 국가 기본값. 국내 탭이면 'KR', 해외 탭이면 빈 값
// (KR 이 프리필되면 해외 등록이 그대로 국내로 새어들어간다 — 운영자가 직접 고르게).
function emptyForm(scope?: 'domestic' | 'overseas'): FormState {
  return {
    displayName: '',
    legalName: '',
    segment: CustomerSegment.INDEPENDENT_RESTAURANT as string,
    countryCode: scope === 'overseas' ? '' : 'KR',
    isShowing: '',
    expectedStoreCount: '',
    addressLine: '',
    addressMapUrl: '',
    notes: '',
    primaryContactName: '',
    primaryContactPosition: '',
    primaryContactPhone: '',
    primaryContactEmail: '',
    robots: [emptyRobotRow()],
    salesOwnerCode: '',
    rentalCompany: '',
    hasInsurance: '',
    contractType: '',
    contractedAt: '',
    endDate: '',
    rentalTermMonths: '',
    depositKrw: '',
    subsidyKrw: '',
    referralFeeKrw: '',
    subscriptionStatus: '',
    grillSelectorType: '',
    installScheduledAt: '',
  };
}

function ynToBool(v: 'O' | 'X' | ''): boolean | undefined {
  if (v === 'O') return true;
  if (v === 'X') return false;
  return undefined;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Phase 3 — 편집 모드 진입 시 기존 행 데이터. undefined 이면 create 모드.
   * 시트 행 클릭 또는 detail 페이지 "편집" 버튼에서 주입.
   */
  editing?: CustomerMasterRow;
  /**
   * round11 (요구 11) — 등록 시 현재 국내/해외 탭. 국가 기본값과 검증에만 쓴다
   * (편집 모드에선 row.countryCode 가 우선).
   */
  scope?: 'domestic' | 'overseas';
}

/**
 * 계약 사실화 1단계 정정5(26-07-29) — 줄의 **원문**이 공통 '계약 형태' 칸의 두 단어로
 * 정확히 표현되는가. 표현되면 그 단어, 아니면 null.
 *
 * 종전엔 서버가 접어 준 표기(`row.contract.contractType`)가 한 단어인지 보고 그대로
 * 공통 칸에 넣었다. 그 값은 7값을 2값으로 접은 것이라 **파일럿 계약도 '렌탈'로 왔고**,
 * 그것을 공통 칸에 넣는 순간 화면이 "이 계약은 렌탈"이라 말하게 됐다 → 줄을 추가하면
 * 새 줄이 그 '렌탈'을 물려받아 구독으로 저장됐다(갈래 2). 이제 화면은 접힌 표기를
 * 읽지 않고 **줄의 원문**만 본다.
 *
 * 계약 사실화 1단계 정정7(26-07-29) 결함 ③ — 정본으로 합치지 **않는** 이유(판단 근거).
 * 정정7 은 반대 방향(단어→원문)의 지역 사본을 지우고 정본을 부르게 했는데, 이 함수와
 * 아래 foldReasonToCommonWord 는 같이 없애지 않았다:
 *   ① 방향이 반대다(원문→단어). 정본 파일(packages/shared contract-line-reason)에 이
 *      방향의 함수는 없고, 서버 쪽 짝(customer-master.row isFoldableLineReason ·
 *      lineReasonToContractType)은 apps/api 안에 있어 화면이 부를 수 없다. 화면이 부를
 *      수 있게 하려면 그 두 함수를 shared 로 옮겨야 하는데, 그것은 서버 표기 경로
 *      (row.robots[].contractType)를 건드리는 별건 공사다.
 *   ② 두 함수가 만드는 것은 **표기가 아니라 이 칸이 서버로 보내는 문자열**('렌탈'/'판매')
 *      이다 — 표기 정본(lib/contract-reason)으로 수렴할 대상이 아니다(위 LINE_REASON_
 *      OPTIONS 주석의 ⚠ 참조).
 *   ③ 둘의 규칙이 서로 **의도적으로 다르다** — 이 함수는 접을 수 없는 값에 null(엄격),
 *      아래는 나머지 전부를 '렌탈'로 접는다(손실). 한 벌로 합치면 그 차이가 사라지고,
 *      그 차이가 바로 정정5 가 세운 안전장치다(엄격한 쪽만 공통 칸을 채우고, 손실이 있는
 *      쪽은 '되돌아온 메아리'를 알아보는 데만 쓴다).
 */
function reasonToCommonWord(reason: string): string | null {
  return reason === 'SALE' ? '판매' : reason === 'LEASE_OPERATING' ? '렌탈' : null;
}

/**
 * 계약 사실화 1단계 정정5(26-07-29) — 원문을 공통 칸의 두 단어로 **접는다**(서버
 * `lineReasonToContractType` 과 같은 규칙).
 *
 * ⚠ 쓰임은 **딱 하나** — "들어온 공통값이 이 줄의 기존 값을 접은 결과인가"(= 화면이
 * 접어 보여 준 값이 그대로 돌아온 것이므로 바꾸라는 뜻이 아니다)를 판정하는 것.
 * 원본을 **지키는** 방향이라 예외로 허용된다. 상속·저장·표기의 근거로는 쓰지 말 것
 * (그것이 이번 정정이 닫은 결함이다 — 파일럿이 '렌탈'로 보이고 실제로 렌탈이 됐다).
 */
function foldReasonToCommonWord(reason: string): string {
  return reason === 'SALE' ? '판매' : '렌탈';
}

/**
 * 계약 사실화 1단계 정정(26-07-28) — 줄별 '계약 형태' 칸의 선택지.
 *
 * 계약 등록 마법사는 7값(DeploymentReason)을 전부 고르게 하는데 고객사 화면은
 * '렌탈/판매' 2값만 보여 줬다. 그래서 파일럿 줄이 '렌탈'로 보이고, 그 값이 저장에
 * 되돌아가 **원본을 파일럿에서 렌탈로 바꿨다**. 화면이 사실을 말하게 하려면 7값을
 * 전부 노출해야 한다.
 *
 * 계약 사실화 1단계 정정4(26-07-29) — 여기 있던 **다섯 번째 라벨 세트**를 지웠다.
 * 종전엔 LEASE_OPERATING='렌탈' / LEASE_FINANCE='금융리스' 라는 자체 문구를 들고
 * 있었고, 정정3 은 "이 화면 시험이 라벨 문구를 통째로 단정하고 있다"는 이유로 그것을
 * 남겼다 — 그 세트와 그 시험이 둘 다 같은 라운드에 새로 들어온 것이라 자기 순환인
 * 근거였다. 이 화면은 오너가 판매/구독을 입력하는 **주 경로**이므로 다른 화면과 다른
 * 단어를 쓰면 안 된다. 이제 라벨은 표기 정본(lib/contract-reason)에서 그대로 가져온다.
 *
 * 어느 세트인가 — 여기는 값을 **고르는** 자리이고 리스 두 종류가 목록에 나란히
 * 놓이므로, 정본이 그 자리용으로 둔 긴 표기(CONTRACT_REASON_OPTION_LABEL:
 * '구독(운용리스)' / '구독(금융리스)')를 쓴다. 계약 등록 마법사 '배치 사유' ·
 * 로봇 배치 모달 '사유' 와 같은 세트라, 값을 고르는 세 화면이 같은 말을 한다.
 *
 * ⚠ 이 줄별 칸과 아래 **공통 '계약 형태'** 칸은 성질이 다르다 — 섞지 말 것.
 *   • 줄별 칸(여기)   : 값이 **원문 enum**(DeploymentReason 7값)이고 화면 문구는
 *                      표기일 뿐이다 → 문구를 정본으로 바꿔도 저장되는 값은 그대로.
 *   • 공통 칸(cm-ctype): 값이 **'렌탈' / '판매' 라는 한국어 문자열 자체**이고 그것이
 *                      서버로 가 저장된다(CreateCustomerMasterDto.contractType, 정본
 *                      contractTypeToLineReason·reasonToCommonWord·foldReasonToCommonWord
 *                      가 그 어휘를 읽는다) → 그 칸의 '렌탈'은 표기가 아니라 **계약**
 *                      이므로 건드리면 저장이 깨진다. 정본 수렴 대상이 아니다.
 *                      계약 사실화 1단계 정정5(26-07-29) — 반면 안내문의 요약
 *                      ('파일럿 1 · 구독 2')은 **표기**이므로 정본 어휘를 쓴다.
 *
 * 순서 — 실제로 거의 전부인 2값을 앞에 두고 나머지는 뒤로 보내 입력 동선을 해치지
 * 않는다(정본의 선언 순서와 다른 것은 순서뿐이고 문구는 정본 그대로다).
 */
const LINE_REASON_ORDER: ReadonlyArray<keyof typeof CONTRACT_REASON_OPTION_LABEL> = [
  'LEASE_OPERATING',
  'SALE',
  'LEASE_FINANCE',
  'PILOT',
  'DEMO',
  'EVENT',
  'INTERNAL',
];
const LINE_REASON_OPTIONS: ReadonlyArray<{ value: string; label: string }> = LINE_REASON_ORDER.map(
  (value) => ({ value, label: CONTRACT_REASON_OPTION_LABEL[value] }),
);

// 계약 사실화 1단계 정정(26-07-28) — 공통 '계약 형태' 한 단어가 가리키는 원문. 공통
// 칸은 2값만 표현하므로 줄의 원문이 이 값과 **정확히 같을 때만** 그 줄을 "공통값을
// 따르는 중"으로 볼 수 있다(파일럿 줄은 공통 '렌탈'로 대신 말할 수 없다).
//
// 계약 사실화 1단계 정정7(26-07-29) 결함 ③ — 여기 있던 지역 함수 commonWordToReason 을
// 지웠다. 그것은 정본(packages/shared contractTypeToLineReason)의 **글자까지 같은
// 미러**였다(`word === '판매' ? 'SALE' : word === '렌탈' ? 'LEASE_OPERATING' : ''`).
// 지금은 동작이 갈리지 않는다 — 공통 칸 Select 가 '' / '렌탈' / '판매' 세 값만 만들므로
// 두 규칙의 답이 늘 같았다. 위험은 **다음 번 넓힘**이었다: 정본 주석이 이미 "받는 어휘를
// 넓히면"의 파급을 적고 있는데(한 필드에 'SALE'/'RENTAL' 영문이 섞여 저장돼 있다), 정본만
// 넓어지면 이 화면의 미리보기·상속 판정은 종전 2단어만 알아보고 서버 저장은 새 어휘를
// 받아들여 **미리보기와 저장 결과가 갈린다**. 이 화면은 이미 @bhops/shared 를 부르므로
// (같은 파일에서 CustomerSegment·UserRole) 정본을 직접 부르는 데 새 배선이 필요 없었다.
//
// ⚠ 반대 방향(값→단어)인 reasonToCommonWord·foldReasonToCommonWord 는 **함께 없애지
// 않았다** — 정본에 대응짝이 없고, 서버 쪽 짝(customer-master.row
// isFoldableLineReason·lineReasonToContractType)은 apps/api 안에 있어 화면이 부를 수
// 없다. 게다가 그 둘이 만드는 것은 표기가 아니라 **이 칸이 서버로 보내는 문자열**이므로
// (위 ⚠ 참조) 표기 정본으로 수렴할 대상도 아니다. 자세한 근거는 그 두 함수 주석에 적었다.

/**
 * 계약 사실화 1단계 정정5(26-07-29) — 줄 하나가 **무엇으로 저장될지**. 서버의 결정
 * 규칙(customer-master.service `resolveLineReason` · `syncContractLines`)을 화면에서
 * 그대로 따라 계산한다. 화면이 약속한 것과 실제 저장이 어긋나지 않게 하는 것이 목적이다.
 *
 *   • 'chosen'   — 줄 칸에서 값을 골랐다(원문 전송). 그 값이 최우선.
 *   • 'common'   — 공통 '계약 형태'가 이 줄에 적용된다.
 *   • 'keep'     — 기존 값이 유지된다. 공통 칸의 두 단어로는 이 줄의 형태를 말할 수
 *                  없어서(파일럿 등) 공통값이 상속 소스가 될 자격이 없는 경우다.
 *                  '공통값 따름'은 "바꾸지 말라"는 뜻으로 읽는다.
 *   • 'inherit'  — 새 행이다. 기존 줄이 전부 같은 형태면 서버가 그 값을 물려준다.
 *   • 'default'  — 근거가 하나도 없다. 서버가 종전 기본값(구독)으로 기록하며 경고 로그를
 *                  남긴다. 화면은 "고르지 않으면 구독으로 저장"이라 미리 말해 준다.
 */
type LineTypePlan =
  | { kind: 'chosen'; reason: string }
  | { kind: 'common'; word: string; reason: string }
  | { kind: 'keep'; reason: string }
  | { kind: 'inherit'; reason: string }
  | { kind: 'default'; reason: string };

function planLineType(args: {
  /** 줄 칸에서 고른 원문('' = 공통값 따름). */
  chosen: string;
  /** 이 줄이 DB 에 갖고 있는 원문(새 행이면 null). */
  saved: string | null;
  /** 기존 줄이 전부 같은 형태일 때 그 원문(없으면 null) — 새 행이 물려받을 값. */
  inherited: string | null;
  /** 공통 '계약 형태' 칸의 값('' = 선택 안 함). */
  commonWord: string;
}): LineTypePlan {
  if (args.chosen) return { kind: 'chosen', reason: args.chosen };
  const base = args.saved ?? args.inherited;
  // 정정7 결함 ③ — 규칙 정본. 알아보지 못하는 단어는 null(신호 없음)이라 종전 ''와 같은
  // 갈래를 탄다(공통 칸이 상속 소스가 되지 않는다).
  const commonReason = contractTypeToLineReason(args.commonWord);
  if (commonReason) {
    // 서버와 같은 판정 — 공통 칸의 값이 **기존/물려받을 값을 접은 결과**라면 그것은
    // 화면이 접어 보여 준 값이 되돌아온 것일 뿐이므로 변경으로 읽지 않는다(접힌 메아리
    // 보호 — 서버 isFoldedEcho). 접기로 정확히 표현되는 값(SALE·LEASE_OPERATING)은
    // 결과가 어차피 같으므로 '공통값'으로 표기한다(그쪽이 운영자에게 정확하다).
    const isEcho =
      !!base && foldReasonToCommonWord(base) === args.commonWord && base !== commonReason;
    if (!isEcho) return { kind: 'common', word: args.commonWord, reason: commonReason };
  }
  if (base) return args.saved ? { kind: 'keep', reason: base } : { kind: 'inherit', reason: base };
  // 서버의 종전 기본값. 값→문구는 정본에서.
  return { kind: 'default', reason: 'LEASE_OPERATING' };
}

/** 정정5 — 정본 표기(짧은 세트). 모르는 값은 원문 그대로(조용히 뭉개지 않는다). */
function reasonLabel(reason: string): string {
  return CONTRACT_REASON_LABEL[reason as keyof typeof CONTRACT_REASON_LABEL] ?? reason;
}

/**
 * 계약 사실화 1단계 정정5(26-07-29) — 줄 칸을 '공통값 따름'으로 둔 줄 옆의 회색 글씨.
 * **저장될 값**을 말한다(조용한 기본값 금지 · 화면이 지키지 못할 약속 금지).
 */
function lineTypeNote(plan: LineTypePlan): string {
  switch (plan.kind) {
    case 'common':
      return `공통값: ${plan.word}`;
    case 'keep':
      // 공통 칸의 두 단어로는 이 줄의 형태를 말할 수 없다 → 기존 값이 유지된다.
      return `기존 값 유지: ${reasonLabel(plan.reason)}`;
    case 'inherit':
      return `기존 줄과 같이: ${reasonLabel(plan.reason)}`;
    case 'default':
      // 물려받을 값도 공통값도 없다. 서버는 기본값으로 기록하고 경고 로그를 남긴다.
      return `고르지 않으면 ${reasonLabel(plan.reason)}으로 저장됩니다`;
    default:
      // 'chosen' 은 줄 칸에 값이 보이므로 이 표기가 뜨지 않는다(호출부 조건).
      return '';
  }
}

/**
 * 계약 사실화 1단계(26-07-28) — 줄들의 계약 형태가 갈렸는지(혼합인지).
 * 계약 사실화 1단계 정정(26-07-28) — 원문(7값) 기준. 파일럿과 렌탈은 다른 형태다.
 * 계약 사실화 1단계 정정5(26-07-29) — 판정 대상을 "**저장될 값**"으로 바꿨다. 종전엔
 * 줄 칸이 빈 줄을 공통값으로만 해석해서, 공통 칸이 비어 있으면 그 줄이 판정에서 아예
 * 빠졌다(파일럿+판매 계약에서 판매 줄을 '공통값 따름'으로 두면 혼합이 안 보였다).
 */
function effectiveLineTypes(plans: ReadonlyArray<LineTypePlan>): string[] {
  return Array.from(new Set(plans.map((p) => p.reason)));
}

/**
 * 계약 사실화 1단계(26-07-28) — 혼합 상태의 대수 요약.
 *
 * 계약 사실화 1단계 정정5(26-07-29) — **원문 값 기준**으로 세고 문구는 표기 정본에서
 * 가져온다("파일럿 1 · 구독 2"). 종전엔 판매/렌탈 2값으로 접어 세서, 파일럿+구독 혼합이
 * "판매 0 · 렌탈 3" 같은 이상한 문장이 됐다(같은 계약을 서버 목록은 '렌탈' 한 단어라
 * 말했으므로 화면과 서버가 서로 반대였다). 서버 요약(원문 값·대수)과 이 문구는 값과
 * 대수가 같고 어휘만 다르다 — 서버는 번역하지 않고 화면이 정본으로 번역한다.
 */
function mixedLineTypeSummary(
  robots: ReadonlyArray<RobotRow>,
  plans: ReadonlyArray<LineTypePlan>,
): string {
  const byReason = new Map<string, number>();
  robots.forEach((r, i) => {
    const reason = plans[i]!.reason;
    byReason.set(reason, (byReason.get(reason) ?? 0) + r.quantity);
  });
  return Array.from(byReason, ([reason, qty]) => `${reasonLabel(reason)} ${qty}대`).join(', ');
}

/**
 * 계약 사실화 1단계 정정8(26-07-29) 결함 ① — **계약이 없는 고객사의 로봇 줄 프리필.**
 *
 * 무슨 일이 있었나: `row.robots` 는 **계약 줄에서만** 온다(서버 row 파생이
 * `latestContract?.lines.map(...) ?? []`). 그래서 계약이 없는 고객사는 늘 빈 배열이고,
 * 편집 창은 그 자리를 기본 행 하나(GEN2 1대)로 채웠다. 그런데 같은 row 는 그 고객사의
 * **딜 견적**(`dealQuote.models`)을 이미 들고 있었고 — 이 화면은 그것을 한 번도 읽지
 * 않았다. 그 결과 견적이 'ZEPHYR 3대'인 고객사를 열면 화면에 'GEN2 1대'가 떠 있었고,
 * 운영자는 그것을 원래 값으로 읽었다(사실과 다른 화면).
 *
 * 이제 계약 줄이 없으면 딜 견적의 모델·수량을 그대로 보여 준다. 나머지 칸(세팅·불판·
 * 사양·가격)은 견적에 없는 값이므로 비운다 — 견적에는 모델과 대수만 있다.
 *
 * ⚠ 줄 id(lineId)는 붙이지 않는다. 이 줄들은 계약 줄이 아니므로 가리킬 신원이 없다.
 *   서버는 id 없는 줄을 새 줄로 확정한다(신원 선언 usesLineIds).
 * ⚠ 이 줄들이 **저장 트리거가 되지는 않는다** — 로봇 줄을 손대지 않은 저장은 robots 를
 *   아예 보내지 않는다고 화면이 선언하기 때문이다(아래 robotsTouched). 그래서 견적을
 *   보여 주는 것만으로 계약이 새로 만들어지거나 견적이 덮이는 일은 없다.
 */
function dealQuoteToRobotRows(row: CustomerMasterRow): RobotRow[] | null {
  const models = row.dealQuote?.models ?? [];
  if (models.length === 0) return null;
  return models.map((m) => ({
    ...emptyRobotRow(),
    modelCode: m.modelCode,
    quantity: m.quantity,
  }));
}

// Phase 3 helper — 편집 모드 진입 시 row 데이터를 form state 로 변환.
function rowToForm(row: CustomerMasterRow): FormState {
  const ynFrom = (v: boolean | null): 'O' | 'X' | '' => (v === true ? 'O' : v === false ? 'X' : '');
  // 계약 사실화 1단계(26-07-28) — 계약 형태 prefill 의 갈림길(이 모달의 가장
  // 위험한 지점: 계약 형태 칸은 **쓰기 경로**다).
  //   • 전 줄이 같으면  → 공통 칸에 그 한 단어, 줄별 칸은 비움(= 공통값 상속).
  //     줄 칸이 값으로 가득 차지 않아 대부분의 고객사는 화면이 종전과 같다.
  //   • 줄별로 갈리면    → 공통 칸은 비우고(혼합은 한 단어로 표현 불가) 줄별 칸에
  //     각자의 값을 채운다. 그래야 다른 항목만 고쳐 저장해도 혼합이 유지된다.
  //
  // 계약 사실화 1단계 정정(26-07-28) — 판정을 **원문(7값)** 으로 한다. 공통 칸은
  // '렌탈/판매' 2값만 표현하므로, 줄의 원문이 그 한 단어가 가리키는 값과 **정확히
  // 같을 때만** 줄 칸을 비운다. 파일럿·시연·행사·사내·금융리스 줄은 공통 칸이 대신
  // 말해 줄 수 없으므로 줄 칸에 제 이름('파일럿' 등)이 뜬다 — 화면이 사실을 말하고,
  // 저장에는 그 원문이 실려 나가므로 원본이 렌탈로 덮이지 않는다.
  //
  // 계약 사실화 1단계 정정5(26-07-29) — 공통 칸의 값을 **줄의 원문에서** 세운다.
  // 종전엔 서버가 접어 준 표기(row.contract.contractType)를 한 단어인지만 보고 넣었고,
  // 그 표기는 파일럿 계약도 '렌탈'이라 말했다. 그래서 파일럿 계약을 열면 공통 칸에
  // '렌탈'이 떠 있었고 — 줄을 하나 추가하면 새 줄이 그 '렌탈'을 물려받아 **구독으로
  // 저장**됐다(갈래 2). 이제 전 줄이 같고 그 원문이 두 단어로 정확히 표현될 때만
  // 공통 칸을 채운다(파일럿 계약은 공통 칸이 빈다 — 그것이 사실이다).
  const lineReasons = new Set(row.robots.map((r) => r.reason));
  const singleLineReason = lineReasons.size === 1 ? [...lineReasons][0]! : null;
  const commonWord = (singleLineReason && reasonToCommonWord(singleLineReason)) ?? '';
  // 정정7 결함 ③ — 규칙 정본(위 import 주석). 종전 지역 미러와 답이 같다.
  const commonReason = contractTypeToLineReason(commonWord);
  return {
    displayName: row.displayName,
    legalName: row.legalName || '',
    segment: row.segment || CustomerSegment.INDEPENDENT_RESTAURANT,
    countryCode: row.countryCode || 'KR',
    isShowing: ynFrom(row.isShowing),
    // S1 Phase 5 (R2 #3) — row 가 expectedStoreCount 를 노출하므로 edit prefill.
    // number → string (input value 용). null 이면 빈 문자열.
    expectedStoreCount: row.expectedStoreCount != null ? String(row.expectedStoreCount) : '',
    addressLine: row.address ?? '',
    addressMapUrl: row.addressMapUrl ?? '',
    notes: row.notes ?? '',
    primaryContactName: row.primaryContactName ?? '',
    primaryContactPosition: row.primaryContactPosition ?? '',
    primaryContactPhone: row.primaryContactPhone ?? '',
    primaryContactEmail: row.primaryContactEmail ?? '',
    robots:
      row.robots.length > 0
        ? row.robots.map((r) => ({
            // 계약 사실화 1단계 정정(26-07-28) — 줄의 신원을 form 에 보관해
            // 저장 시 되돌려 보낸다(행 삭제·모델 정정에도 값이 옮겨 붙지 않게).
            lineId: r.id,
            modelCode: r.modelCode,
            quantity: r.quantity,
            // 계약 사실화 1단계 정정(26-07-28) — 공통 칸이 이 줄의 형태를 정확히
            // 대신 말할 수 있을 때만 비운다(그 밖엔 원문을 그대로 보여 준다).
            reason: commonReason && r.reason === commonReason ? '' : r.reason,
            setting: r.setting ?? '',
            grillPlate: r.grillPlate ?? '',
            specialParts: r.specialParts ?? '',
            wireGrill: r.wireGrill ?? '',
            // Phase 5 — per-line 가격 (multi-model 시 정확).
            // backend `row.robots[].unitPriceKrw / listPriceKrw` 직접 사용.
            // 계약 사실화 1단계 정정(26-07-28) — 0 은 빈 칸으로 보여 준다. 도입료
            // 컬럼이 NOT NULL 이라 '비움'이 0 으로 저장되므로, 그것을 '0'으로 되읽으면
            // 지운 칸에 0 이 되살아난 것처럼 보인다(왕복이 안정적이지 않다).
            unitPriceKrw: r.unitPriceKrw ? String(r.unitPriceKrw) : '',
            listPriceKrw: r.listPriceKrw ? String(r.listPriceKrw) : '',
            // S1 Phase 5 (R2 #3) — per-line Store ID 를 row.robots[] 에서
            // prefill (round-trip). null 이면 빈 문자열.
            // FB2 §4B — 희망 색상/불감지 입력 제거. 희망 방향만 prefill 유지.
            storeId: r.storeId ?? '',
            desiredDirection: r.desiredDirection ?? '',
          }))
        : // 계약 사실화 1단계 정정8(26-07-29) 결함 ① — 계약 줄이 없으면 **딜 견적**을
          // 보여 준다(위 dealQuoteToRobotRows 주석). 견적도 없으면 종전대로 기본 행 하나.
          (dealQuoteToRobotRows(row) ?? [emptyRobotRow()]),
    salesOwnerCode: row.salesOwnerCode ?? '',
    // 운영자 피드백 (round 3) — rentalCompany 가 있으면 그 값을, 없으면
    // 기존 hasAJ Boolean 에서 fallback 매핑 (true → 'AJ', false → '해당없음').
    rentalCompany:
      row.rentalCompany ?? (row.hasAJ === true ? 'AJ' : row.hasAJ === false ? '해당없음' : ''),
    hasInsurance: ynFrom(row.hasInsurance),
    // 계약 사실화 1단계(26-07-28) — 혼합이면 공통 칸을 비운다(한 단어로 표현 불가).
    // 혼합 사실은 계약 형태 칸의 안내문으로 보여 주고(아래 contractTypeHint), 실제
    // 값은 줄별 칸이 들고 있다. 정정5(26-07-29) — 접을 수 없는 형태(파일럿 등)만인
    // 계약도 공통 칸을 비운다(위 commonWord 주석).
    contractType: commonWord,
    contractedAt: toYmdInput(row.contract?.contractedAt),
    endDate: toYmdInput(row.contract?.endDate),
    rentalTermMonths: row.contract?.rentalTermMonths?.toString() ?? '',
    depositKrw: row.contract?.depositKrw?.toString() ?? '',
    // 개선 9건 차수2(26-08-04) §7 — 지원금·소개비 prefill. 보증금과 같은 관례
    // (`?? ''`)라 값이 없으면 빈 칸이고, **0 은 '0' 으로 채워** 명시적 0원이 편집
    // 창에서 사라지지 않는다(0 을 '없음'으로 접는 것은 목록·상세의 표기 규칙이고
    // 입력 칸은 저장된 사실을 그대로 보여야 한다 — 보증금이 세운 관례와 동일).
    subsidyKrw: row.contract?.subsidyKrw?.toString() ?? '',
    referralFeeKrw: row.contract?.referralFeeKrw?.toString() ?? '',
    subscriptionStatus: row.contract?.subscriptionStatus ?? '',
    grillSelectorType: row.grillSelector.type ?? '',
    // round5 #2b④ — row 에 노출되어 편집 시 prefill (이전엔 항상 빈 칸 →
    // '저장 안 됨' 으로 체감되던 원인).
    installScheduledAt: toYmdInput(row.contract?.installScheduledAt),
  };
}

/**
 * 계약 사실화 1단계 정정8(26-07-29) 결함 ① — 이 화면이 저장에 싣는 body.
 *
 * 등록 body 와 두 군데 다르다:
 *   • `robots` 가 optional — 로봇 줄을 손대지 않은 편집 저장은 줄을 **아예 싣지 않는다**.
 *   • `robotsTouched` — 그 사실을 서버에 **선언**한다(서버 DTO UpdateCustomerMasterDto).
 *     서버는 이 선언만 보고 갈라며, payload 모양으로 짐작하지 않는다.
 *
 * 두 신호가 같은 말을 하는 것은 일부러다 — 선언이 무시되더라도 값이 실려 있지 않으므로
 * 견적이 파괴되지 않고, 값이 실려 있어도 선언이 false 면 서버가 쓰지 않는다.
 *
 * ⚠ `robotsTouched` 는 등록(POST)에는 싣지 않는다 — 등록 DTO 에 없는 키를 보내면
 *   전역 ValidationPipe(forbidNonWhitelisted)가 **저장 전체를 400** 으로 막는다.
 *   그래서 아래 submit 은 편집일 때만 이 키를 넣는다.
 *
 * 계약 사실화 1단계 정정9(26-07-29) 결함 ① — 세 번째로 `contractTypeTouched` 가 붙었다.
 * 공통 '계약 형태'는 **로봇 줄에 적용되는 값**인데, 정정8 이후 줄을 손대지 않은 저장은
 * 줄을 싣지 않으므로 서버가 적용할 대상을 잃었다(스윕 동선이 무동작이 됐다). 그 값을
 * 줄에 적용하려면 줄 배열이 필요하지 않으므로(서버가 저장된 줄을 근거로 쓴다) 줄을
 * 싣는 대신 **바꿨다는 사실만** 선언한다 — 아래 상태 주석에 근거를 적었다.
 * 이 키도 등록 DTO 에 없으므로 편집일 때만 넣는다.
 *
 * 개선 9건 차수2g(26-08-04) 결함 ㉠ — 네 번째로 `expectedContractId` 가 붙었다. 이 창의
 * 계약 칸은 **목록을 받아온 시점의 최신 계약** 값으로 프리필되는데, 서버는 저장 시점에
 * **다시** 최신 계약을 고른다. 그 사이 같은 고객사에 계약이 하나 등록되면(추가 판매를 새
 * 계약으로 등록하는 지금 규칙에서는 흔한 일이다) 프리필의 출처와 저장 대상이 다른 계약이
 * 되어 보증금·지원금·소개비가 엉뚱한 계약에 박힌다. 그래서 **어느 계약을 고치는 중인지**
 * 선언하고, 서버는 어긋나면 저장하지 않고 409 로 거절한다(서버 DTO 주석에 근거).
 * 이 키도 등록 DTO 에는 없으므로 편집일 때만 넣는다.
 */
type SaveBody = Omit<CreateCustomerMasterBody, 'robots'> & {
  robots?: CreateCustomerMasterRobotLine[];
  robotsTouched?: boolean;
  contractTypeTouched?: boolean;
  // 프리필의 출처가 된 계약의 신원. 계약이 없던 고객사는 null(= "계약이 없었다"는 선언).
  expectedContractId?: string | null;
};

export function CustomerMasterCreateModal({ open, onClose, editing, scope }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  // 개선 9건 차수2c(26-08-04) §5 — 새 계약 등록으로 그 자리에서 이동.
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  // SALES role 은 shared/enums.ts 에 존재하지 않음 — SALES_MANAGER 로 일원화.
  // readonly UserRole[] 로 캐스팅해 `.includes(role)` 의 narrow 충돌 회피.
  const PRICE_VISIBLE_ROLES: readonly UserRole[] = [
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.SALES_MANAGER,
  ];
  const canSeePrice = role && PRICE_VISIBLE_ROLES.includes(role);
  const isEdit = !!editing;

  const [f, setF] = useState<FormState>(() => (editing ? rowToForm(editing) : emptyForm(scope)));
  const [error, setError] = useState<string | null>(null);
  // 2026-09-30 — 안내(FormError)는 창 **맨 위**에 뜨는데 창 본문은 스크롤된다. 맨 아래 칸
  // ('희망 설치 일정')을 적고 등록을 누르면 안내가 화면 밖에 떠 버튼이 무반응처럼 보였다.
  // 막힐 때마다 안내 자리로 스크롤한다. submitCount 를 함께 보는 이유: 같은 이유로 다시
  // 누르면 문구가 같아 error 가 안 바뀌므로 그것만 보면 두 번째 클릭은 또 무반응이 된다.
  const errorRef = useRef<HTMLDivElement>(null);
  const [submitCount, setSubmitCount] = useState(0);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error, submitCount]);
  // ============================================================
  // 계약 사실화 1단계 정정8(26-07-29) 결함 ① — 로봇 줄을 **손댔는지**
  // ============================================================
  //
  // 이 화면은 등록이든 편집이든 robots 를 항상 보냈다. 계약이 없는 고객사는 계약 줄이
  // 없어 기본 행 하나(GEN2 1대)로 프리필됐으므로, **전화번호만 고쳐 저장해도** 그 GEN2
  // 1대가 서버로 나가 딜 견적(ZEPHYR 3대)을 GEN2 1대로 덮고 예상 대수를 1 로 만들고
  // GEN2 1줄짜리 계약까지 새로 만들었다. 화면에 GEN2 가 떠 있으니 운영자는 그것이 원래
  // 값이라 읽었고 — 알아차릴 방법이 없었다.
  //
  // 그래서 "손대지 않았다"를 화면이 **선언한다**(서버 DTO robotsTouched). 서버가 payload
  // 모양으로 짐작하게 만들지 않는다 — 이 차수의 1~5라운드가 그 추론으로 네 번 연속
  // 결함을 냈고, 4라운드가 줄 신원(usesLineIds)에서 세운 관례가 바로 이 방식이다.
  //
  // 무엇이 '손댐'인가: 로봇 섹션의 값 변경 · 행 추가 · 행 삭제. 그 밖의 어떤 칸도
  // 로봇 줄을 손댄 것으로 세지 않는다(공통 '계약 형태' 칸은 계약 필드이고, 그 값이
  // 줄에 어떻게 적용되는지는 서버가 저장된 줄을 근거로 판단한다).
  const [robotsTouched, setRobotsTouched] = useState(false);
  // ============================================================
  // 계약 사실화 1단계 정정9(26-07-29) 결함 ① — 공통 '계약 형태'를 **바꿨는지**
  // ============================================================
  //
  // 정정8 이후 로봇 줄을 손대지 않은 저장은 줄을 싣지 않는다. 그런데 서버는 줄 저장을
  // `robots` 유무로 가르므로, **공통 '계약 형태' 변경이 통째로 무동작이 됐다** — 목록에서
  // '구독'으로 잘못 적힌 판매 고객사를 열어 이 칸을 '판매'로 바꿔 저장하면 성공 토스트만
  // 뜨고 계약 줄이 하나도 바뀌지 않았다(감사 로그도, 서버 로그도 없었다). 그것이 plan.md
  // §1 이 만들려던 정리 스윕 동선 자체다.
  //
  // 왜 `robotsTouched` 를 넓히지 않는가 — 넓히면 이 화면이 `robots` 를 실어 보내게 되고,
  // 그 줄들이 딜 견적(DealModelLine)·예상 대수를 전량 교체한다. "그 줄은 딜 견적에서
  // 세운 프리필이니 견적과 같은 값"이라는 논리는 **두 자리에서 깨진다**:
  //   ① 계약이 있는 고객사의 프리필은 **계약 줄**이다(rowToForm 은 row.robots 가 비어
  //      있을 때만 견적을 본다) — 계약 줄과 딜 견적이 다르면 견적이 조용히 덮인다.
  //   ② 계약도 견적도 없으면 프리필이 기본 행 하나(GEN2 1대)다 — 없던 견적이 만들어진다.
  // 그래서 줄은 싣지 않고 **바꿨다는 사실만** 따로 선언한다(서버 DTO contractTypeTouched).
  // 서버는 그 선언을 보고 **저장된 줄**에만 형태를 쓴다(줄을 만들지도 지우지도 않는다).
  //
  // 무엇이 '바꿈'인가: 공통 '계약 형태' 칸을 건드린 것. 바꿨다가 되돌려도 true 로 남지만
  // 서버가 "이미 그 값이면 쓰지 않는다"로 흡수하므로 무해하다(값이 아니라 사실을 말한다).
  const [contractTypeTouched, setContractTypeTouched] = useState(false);

  // FB2 §4B — 모델별 '희망 방향' picklist 를 구동하려면 선택 모델의
  // variantOptionGroupKey/축 라벨을 알아야 한다. RobotForm 과 동일한
  // queryKey(['models']) 를 써서 react-query 가 디듀프한다(추가 네트워크 없음).
  const { data: modelsData } = useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const modelItems = modelsData?.items ?? [];

  // editing prop 이 변하면 (다른 행 편집으로 진입) form 초기화
  useEffect(() => {
    if (editing) {
      setF(rowToForm(editing));
      setError(null);
    } else {
      setF(emptyForm(scope));
      setError(null);
    }
    // 정정8 결함 ① — 다른 고객사로 갈아타면 '손댐'도 초기화한다(앞 고객사에서 줄을
    // 고친 사실이 다음 고객사의 저장에 선언으로 새어 나가지 않게).
    setRobotsTouched(false);
    // 정정9 결함 ① — 공통 형태 '바꿈' 선언도 같은 이유로 초기화한다.
    setContractTypeTouched(false);
  }, [editing, scope]);

  const set =
    <K extends keyof FormState>(k: K) =>
    (e: { target: { value: string } }) =>
      setF((cur) => ({ ...cur, [k]: e.target.value }));

  // 정정9 결함 ① — 공통 '계약 형태' 칸의 유일한 입구. 값 변경과 함께 '바꿈'을 표시한다
  // (선언의 근거가 한 군데로 모인다 — 로봇 줄의 updateRobot·addRobotRow·removeRobotRow 와
  //  같은 관례).
  const setContractType = (e: { target: { value: string } }): void => {
    setContractTypeTouched(true);
    set('contractType')(e);
  };

  // 정정8 결함 ① — 로봇 줄을 바꾸는 **모든** 입구가 여기 셋뿐이므로, 셋 다 '손댐'을
  // 표시한다(선언의 근거가 한 군데로 모인다).
  const updateRobot = (idx: number, key: keyof RobotRow, value: string | number) => {
    setRobotsTouched(true);
    setF((cur) => ({
      ...cur,
      robots: cur.robots.map((r, i) => {
        if (i !== idx) return r;
        const next: RobotRow = {
          ...r,
          [key]: key === 'quantity' ? Math.max(1, Number(value) || 1) : value,
        };
        // 2026-09-30 — 모델을 바꾸면 방향 목록(variantOptionGroupKey)이 달라진다. 옛 모델의
        // 방향은 새 목록에 없거나 칸 자체가 사라져(축 없는 모델) **보이지도 지울 수도 없는
        // 값**으로 줄에 남았다 — 그대로 저장되면 센서리스 줄에 GEN2 방향이 붙고, 계약 없는
        // 등록은 그 안 보이는 값 때문에 저장 전 차단에 걸렸다. 사람이 모델을 바꾼 **눈에
        // 보이는 동작**에 따라 비우는 것이라 "안 건드렸는데 값이 바뀐다"가 아니다.
        if (key === 'modelCode' && value !== r.modelCode) {
          const axisOf = (code: string) =>
            modelItems.find((m) => m.code === code)?.variantOptionGroupKey ?? null;
          if (axisOf(String(value)) !== axisOf(r.modelCode)) next.desiredDirection = '';
        }
        return next;
      }),
    }));
  };
  const addRobotRow = () => {
    setRobotsTouched(true);
    setF((cur) => ({ ...cur, robots: [...cur.robots, emptyRobotRow()] }));
  };
  const removeRobotRow = (idx: number) => {
    setRobotsTouched(true);
    setF((cur) => ({
      ...cur,
      robots: cur.robots.length > 1 ? cur.robots.filter((_, i) => i !== idx) : cur.robots,
    }));
  };

  const totalQty = f.robots.reduce((s, r) => s + r.quantity, 0);

  // ============================================================
  // 개선 9건 차수2c(26-08-04) §5 — 추가 판매를 새 계약으로 유도
  // ============================================================
  //
  // 실태(계획서 §5): 기존 고객사에 추가 판매를 넣는 현실적인 경로는 이 창에서 로봇 줄을
  // 붙이는 것뿐이고, 그 줄은 **가장 최근 계약**에 붙으면서 그 계약의 배정 상태를 되돌리지
  // 않는다. 그 계약이 이미 배정을 마쳤다면 추가분은 어느 화면에도 뜨지 않는다.
  //
  // **막지 않고 안내한다**(이 팀 원칙) — 다만 배정이 끝난(INSTALLED) 계약은 서버가
  // 409 로 거부하므로, 저장 버튼을 누르기 **전에** 그 사실과 다음 행동을 알려 준다.
  // '설치 대기'(배정 중)는 서버가 받아 주고 차수1 안전망이 부족분을 배정 인박스에
  // 되살리므로, 같은 자리에 더 부드러운 문장을 쓴다.
  //
  // 무엇을 '추가'로 보는가 — 서버 가드와 **같은 두 가지**:
  //   ① 새 줄(저장된 줄 id 가 없는 행)  ② 저장된 줄의 수량 증가.
  // 줄 삭제·수량 감소는 추가가 아니다(정정이고 숨은 부족분을 만들지 않는다).
  const editingPrimaryContract = editing?.contracts?.find((c) => c.isPrimary) ?? null;
  const savedQtyByLineId = new Map((editing?.robots ?? []).map((r) => [r.id, r.quantity]));
  // 줄 0개 레거시 계약은 대상이 아니다(서버 가드와 같은 예외 — 그 계약에 줄을 넣는 것은
  // 추가 판매가 아니라 실물 기록이다).
  const primaryHasSavedLines = (editing?.robots?.length ?? 0) > 0;
  const addsRobots =
    isEdit &&
    robotsTouched &&
    primaryHasSavedLines &&
    f.robots.some(
      (r) => !r.lineId || r.quantity > (savedQtyByLineId.get(r.lineId) ?? Number.POSITIVE_INFINITY),
    );
  const primaryHandoff = editingPrimaryContract?.handoffStatus ?? null;
  const addsToAssignedContract =
    addsRobots &&
    (primaryHandoff === 'INSTALLED' || primaryHandoff === 'ASSIGNED_AWAITING_INSTALL');
  // 서버가 거부하는 경우(설치 완료)와 받아 주는 경우(설치 대기)를 문장이 구분한다.
  const addBlockedByServer = addsRobots && primaryHandoff === 'INSTALLED';

  // 다계약 고지 — 이 창은 **가장 최근 계약** 하나를 고친다. 계약이 여럿이면 시트의
  // 반납일·D-day 는 다른 계약(가장 임박한 만기)을 보고 있을 수 있으므로, 어느 계약을
  // 고치는 중인지 말해 준다(그 말이 없으면 "시트엔 2026년인데 창엔 2029년"이 된다).
  const editingContracts = editing?.contracts ?? [];
  const soonestOfEditing = editingContracts.find((c) => c.isSoonest) ?? null;
  const showMultiContractNotice = isEdit && editingContracts.length > 1;

  // ============================================================
  // 개선 9건 차수2g(26-08-04) 결함 ㉡ — 시트 금액과 이 창의 금액은 **일부러 다르다**
  // ============================================================
  //
  // 시트의 금액 열은 고객사 합계(row.totals)이고 이 창의 금액 칸은 저장이 닿는 그 계약의
  // 값(row.contract)이다. 그래서 슭돈에서 시트 보증금은 ₩1,500,000 인데 이 창의 보증금
  // 칸은 **빈 칸**이다(그 금액은 옛 구독 계약에 있고, 이 창이 고치는 새 판매 계약에는
  // 없다). 창이 그 사실을 말하지 않으면 운영자는 '누락'으로 읽고 1,500,000 을 입력해
  // 저장한다 — 없던 보증금이 판매 계약에 생기고 시트 합이 ₩3,000,000 이 된다(값이 저장으로
  // 자라는 사고. 차수2f 가 자리를 나눠 닫은 그 사고와 결과가 같다).
  //
  // 시트가 **무엇을 합한 값인지**는 서버가 말해 준 대로만 옮긴다(row.totals.basis /
  // countedContractIds — 시트가 읽는 바로 그 값이다). 여기서 "전 계약 합" 같은 규칙을
  // 다시 적으면 서버의 셈 기준이 바뀔 때 이 문구만 조용히 거짓이 된다.
  const sheetAmountBasisText = ((): string => {
    const t = editing?.totals ?? null;
    // 서버가 합계를 안 내려준 경우(옛 응답) — 아는 사실만 말한다.
    if (!t) return '목록의 금액 열은 이 계약 하나가 아니라 고객사 단위로 합한 값입니다';
    if (t.basis === 'latest-fallback')
      return '목록의 금액 열은 진행 중인 계약이 하나도 없어 가장 최근 계약 1건만 보고 있습니다';
    return `목록의 금액 열은 진행 중인 계약 ${t.countedContractIds.length}건을 합한 값입니다`;
  })();

  // FB §5 — 같은 모델을 여러 줄 입력해도 견적(DealModelLine)에서는 모델당
  // 1행으로 수량이 합산된다(서버에서 집계). 사용자가 놀라지 않도록 중복 모델이
  // 있으면 비차단(non-blocking) 안내만 띄운다. 제출은 막지 않는다.
  const hasDuplicateModels = new Set(f.robots.map((r) => r.modelCode)).size < f.robots.length;

  // 계약 사실화 1단계 정정5(26-07-29) — 각 줄이 **무엇으로 저장될지**를 서버와 같은
  // 규칙으로 미리 계산한다. 화면의 안내문·줄별 표기가 모두 이 하나를 근거로 하므로
  // "화면은 렌탈이라 약속하고 서버는 파일럿을 유지" 같은 어긋남이 생기지 않는다.
  //
  // 저장돼 있는 줄의 원문(savedLineReasons)과 새 행이 물려받을 값(inheritedLineReason)은
  // **편집 대상 row** 에서 읽는다 — 서버가 저장 시점에 DB 의 기존 줄로 계산하는 것과
  // 같은 입력이다(form 의 현재 값이 아니다. 그것은 아직 저장되지 않은 값이므로).
  const savedLineReasons = new Map((editing?.robots ?? []).map((r) => [r.id, r.reason]));
  const inheritedLineReason = (() => {
    const kinds = new Set((editing?.robots ?? []).map((r) => r.reason));
    return kinds.size === 1 ? [...kinds][0]! : null;
  })();
  const linePlans = f.robots.map((r) =>
    planLineType({
      chosen: r.reason,
      saved: (r.lineId && savedLineReasons.get(r.lineId)) || null,
      inherited: inheritedLineReason,
      commonWord: f.contractType,
    }),
  );

  // 계약 사실화 1단계(26-07-28) — 지금 화면의 줄들이 형태로 갈렸는지.
  // 갈렸으면 공통 '계약 형태' 칸은 줄에 적용되지 않으므로(줄별 값이 우선) 그
  // 사실을 안내문으로 말해 준다 — "골랐는데 안 바뀐다"를 없애는 유일한 방법.
  const formLineTypes = effectiveLineTypes(linePlans);
  const isLineTypeMixed = formLineTypes.length > 1;
  // 계약 사실화 1단계 정정6(26-07-29) 결함 ② — 이 칸이 **지키지 못할 약속**을 하지 않게.
  //
  // 종전엔 혼합이 아니면 언제나 "비워 둔 로봇 줄이 이 값을 따릅니다."였다. 그런데 파일럿
  // (·시연·행사·사내·금융리스) 계약을 열어 줄 칸을 '공통값 따름'으로 두고 공통 칸에서
  // '렌탈'을 고르면 저장 결과는 **파일럿**이다(정정5의 접힌 메아리 보호 — planLineType
  // 'keep'). 그 순간 화면이 같은 시각에 두 가지를 말했다 — 줄 옆 회색 글씨 "(기존 값
  // 유지: 파일럿)"(정확) vs 이 힌트(거짓). 정정5 가 줄별 문구에서 없앤 거짓 약속이 여기
  // 남아 있었다. 계산은 이미 linePlans 에 있으므로 그것으로 문구를 가른다.
  //
  // 'keep' 줄이 있으면 이 칸은 그 줄을 바꾸지 못한다 — 공통 칸이 비어 있을 때도, '렌탈'을
  // 골라 메아리로 무시될 때도 같다. 두 경우 모두 사실은 하나다: "그 줄은 안 바뀐다."
  // (반대로 그 줄에 '판매'를 고르면 메아리가 아니므로 실제로 바뀌고 kind 는 'common' 이
  //  되어 이 갈래에 들어오지 않는다 — 그때는 종전 문구가 사실이다.)
  //
  // 계약 사실화 1단계 정정7(26-07-29) 결함 ② — 위 "두 경우 모두 사실은 하나"가 **사유
  // 절에서는 사실이 아니었다.** 정정6 이 두 경우에 같은 문장을 붙였더니, 구독/판매만인
  // 계약을 열어 공통 칸을 '선택 안 함'으로 되돌리면 "기존 값(판매)은 렌탈/판매로 말할 수
  // 없어…"라는 **자기모순**이 떴다 — 판매는 정확히 '판매'로 말할 수 있고, 그 줄이 안 바뀌는
  // 진짜 이유는 공통 칸이 비었다는 것이다. 결론(안 바뀐다)은 두 경우가 같지만 **이유가
  // 다르므로 문장을 가른다**:
  //   (a) 공통 칸에 값이 있는데도 'keep' — 기존 값이 그 두 단어로 말할 수 없는 값(파일럿
  //       등)이라 상속 대상이 아니다. 정정6 문구가 사실인 자리.
  //   (b) 공통 칸이 비어 'keep' — 상속할 값이 애초에 없다. 값의 종류와 무관하다.
  // 갈림 기준은 **정본으로 환산한 결과**를 본다(planLineType 이 보는 것과 같은 값) —
  // 칸의 글자 유무가 아니라 "그 단어가 상속 소스가 될 자격이 있는가"가 사유를 가른다.
  //
  // 계약 사실화 1단계 정정9(26-07-29) 결함 ① — 갈래 하나가 더 필요해졌다. 정정6·7 이 가른
  // 두 문장은 **줄 칸을 '공통값 따름'으로 둔 줄**('keep')이 있을 때의 말이다. 그런데 파일럿
  // 계약을 열면 그 줄 칸에 '파일럿'이 **명시적으로** 채워져 있고(rowToForm — 공통 칸 두
  // 단어로 말할 수 없는 값이므로), 그 상태에서 공통 칸을 '판매'로 바꾸면 'keep' 도 혼합도
  // 아니라 종전 문구("비워 둔 로봇 줄이 이 값을 따릅니다.")가 그대로 떴다 — 따를 줄이 하나도
  // 없는데. 정정9 로 이 칸이 실제로 줄에 적용되기 시작했으므로(선언 contractTypeTouched),
  // 그 거짓 약속은 이제 "고쳤다고 믿었는데 안 고쳐졌다"로 바로 이어진다.
  //
  // 판정은 이미 있는 linePlans 로 한다 — 이 칸을 근거로 저장될 줄('common')이 하나도 없으면
  // 이 칸은 이번 저장에서 아무 것도 바꾸지 않는다. 서버도 같은 결론에 이른다
  // (resolveCommonOnlyLineReason: 저장된 값이 공통 칸 두 단어로 말할 수 없으면 건드리지
  //  않는다 — 정정5 의 보호). 그래서 화면의 말과 저장 결과가 어긋나지 않는다.
  const keptPlan = linePlans.find((p) => p.kind === 'keep');
  const commonInheritSource = contractTypeToLineReason(f.contractType);
  const hasCommonFollower = linePlans.some((p) => p.kind === 'common');
  const contractTypeHint = isLineTypeMixed
    ? `로봇 줄마다 계약 형태가 다릅니다. 지금은 ${mixedLineTypeSummary(f.robots, linePlans)}입니다. 바꾸려면 로봇 줄의 형태 칸에서 고르세요.`
    : keptPlan
      ? commonInheritSource
        ? `비워 둔 로봇 줄은 기존 형태인 ${reasonLabel(keptPlan.reason)} 그대로 남습니다. 이 칸의 렌탈이나 판매로는 바뀌지 않으니, 로봇 줄의 형태 칸에서 골라 주세요.`
        : `계약 형태를 고르지 않아서, 비워 둔 로봇 줄은 기존 형태인 ${reasonLabel(keptPlan.reason)} 그대로 남습니다.`
      : commonInheritSource && !hasCommonFollower
        ? `이 값을 따르는 로봇 줄이 없어 저장해도 계약 형태가 바뀌지 않습니다. 지금 로봇 줄은 ${reasonLabel(formLineTypes[0] ?? '')}입니다. 바꾸려면 로봇 줄의 형태 칸에서 고르세요.`
        : '비워 둔 로봇 줄이 이 값을 따릅니다.';

  const saveMutation = useMutation({
    // 정정8 결함 ① — 편집은 robots 를 생략할 수 있고(선언 동반) 등록은 늘 싣는다.
    // 등록 캐스팅이 안전한 근거는 submit 이 등록 경로에서 반드시 robots 를 채운다는 것.
    mutationFn: (body: SaveBody) =>
      editing
        ? customerMasterApi.update(editing.id, body as UpdateCustomerMasterBody)
        : customerMasterApi.create(body as CreateCustomerMasterBody),
    onSuccess: (row) => {
      // round4 #5a (BUG#5) — 통합 모달은 한 트랜잭션에서 deal/contract/infraOrder/
      // dealModelLine/contractLine 까지 생성·수정하므로 영업칸반·계약목록·인프라
      // 발주 화면이 stale. cascade 로 일괄 무효화.
      invalidateCustomerMasterCascade(qc);
      toast.success(
        isEdit
          ? `${row.displayName} 정보를 저장했습니다.`
          : `${row.displayName} 고객사를 등록했습니다.`,
        isEdit ? '고객사 편집' : '신규 고객사',
      );
      // 개선 9건 차수2c(26-08-04) §5 — 자동 배정 연결이 모호해서 멈췄다면 **그 자리에서**
      // 알린다(종전엔 서버 로그에만 남아 저장한 사람은 알 수 없었다). 저장은 성공했으므로
      // 성공 토스트를 지우지 않고 경고를 하나 더 띄운다 — 사실 두 개를 다 말한다.
      if (row.assignmentLinkWarning) {
        toast.warn(row.assignmentLinkWarning, '로봇 자동 연결 중지');
      }
      if (!isEdit) setF(emptyForm(scope));
      // 정정8 결함 ① — 저장이 끝나면 '손댐'도 초기화한다(같은 창을 다시 열어 저장할 때
      // 앞 저장의 선언이 남아 있지 않게).
      setRobotsTouched(false);
      // 정정9 결함 ① — 공통 형태 '바꿈' 선언도 같이 초기화한다.
      setContractTypeTouched(false);
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    setSubmitCount((n) => n + 1);
    const displayName = f.displayName.trim();
    if (!displayName) {
      setError('상호를 입력해 주세요.');
      return;
    }
    if (!f.salesOwnerCode.trim()) {
      setError('영업 담당을 입력해 주세요.');
      return;
    }
    if (f.robots.length === 0 || totalQty <= 0) {
      setError('도입 로봇 모델을 1대 이상 입력해 주세요.');
      return;
    }
    // round11 (요구 11) — 국가 코드는 서버 DTO 가 @Length(2,2) 라 형식을 먼저 막는다.
    const countryCode = f.countryCode.trim().toUpperCase();
    if (countryCode.length !== 2) {
      setError('국가는 KR, US, JP처럼 2자리 코드로 입력해 주세요.');
      return;
    }
    // 해외 탭에서 KR 로 등록하면 저장 직후 목록(국내 scope)에서 사라져 "저장 안 됨"
    // 으로 보인다. 등록 시점에만 막는다(편집은 국내↔해외 정정 경로라 허용).
    if (!isEdit && scope === 'overseas' && countryCode === 'KR') {
      setError('해외 고객사는 KR이 아닌 국가 코드를 입력해 주세요.');
      return;
    }

    // round5 #2b③ — 편집 모드 클리어 시멘틱. 모달이 ''→undefined 로 바꿔
    // 보내면 서버는 undefined=무변경이라 어떤 필드도 비워서 저장할 수
    // 없었다. 편집 모드에선: 문자열은 ''(서버 `|| null` 이 클리어로 처리),
    // 날짜는 null(서버 null=클리어) 을 전송하되, "원래 값이 있던 필드"
    // 에만 적용 — 무계약 고객사의 빈 입력이 hasContractInput 을 켜서
    // 빈 Contract 를 만들지 않게 한다.
    const hadContract = editing?.contract ?? null;
    const clearStr = (v: string, had: string | null | undefined): string | undefined => {
      const t = v.trim();
      if (t) return t;
      return isEdit && had ? '' : undefined;
    };
    const clearDate = (v: string, had: string | null | undefined): string | null | undefined => {
      if (v) return v;
      return isEdit && had ? null : undefined;
    };
    // 개선 9건 차수2(26-08-04) §7 — 금액 칸의 클리어. clearStr 을 그대로 쓰면 안 된다:
    // 그쪽은 `had` 를 **참/거짓**으로 보므로 저장된 0(명시적 0원)을 '값 없음'으로 읽어
    // 비운 칸이 전송되지 않는다(0 원을 지울 방법이 사라진다). 여기서는 `had != null`
    // 로 판정한다 — 0 도 지울 수 있는 값이다.
    // 빈 문자열을 보내면 서버 DTO 의 @BigIntWire 가 null 로 접어 **지우기**가 된다.
    const clearAmount = (v: string, had: number | null | undefined): string | undefined => {
      const t = v.trim();
      if (t) return t;
      return isEdit && had != null ? '' : undefined;
    };

    // ============================================================
    // 계약 사실화 1단계 정정8(26-07-29) 결함 ① — 로봇 줄을 손댔는지 **선언한다**
    // ============================================================
    //
    // 등록은 로봇 줄이 필수 입력이므로 늘 '손댐'이다. 편집은 위 robotsTouched 상태가
    // 답한다(로봇 섹션의 값 변경 · 행 추가 · 행 삭제만 손댐으로 센다).
    //
    // 손대지 않은 편집 저장은 줄을 **아예 싣지 않는다** — 프리필된 줄이 되돌아가
    // 딜 견적을 덮거나(GEN2 1대) 계약을 새로 만드는 일이 원천적으로 없어진다.
    const robotsDeclaredTouched = !isEdit || robotsTouched;
    const robotLines: CreateCustomerMasterRobotLine[] = f.robots.map(
      (r): CreateCustomerMasterRobotLine => ({
        // 계약 사실화 1단계 정정4(26-07-29) — **이 화면의 신원 규칙을 선언한다.**
        // "줄 id 로 신원을 말하는 화면"이라는 뜻이고, 그래서 서버는 아래 lineId 가
        // 비어 있는 행을 '새 행'으로 확정한다(순서 폴백 금지).
        //
        // ⚠ 행 구성과 **무관하게 항상 true** 다 — 기존 행을 전부 지우고 새 행만 남긴
        //   저장(lineId 0개)에서도 true. 종전(정정3)엔 서버가 "payload 에 lineId 가
        //   하나라도 있는지"로 이것을 추론했는데, 바로 그 저장에서 신호가 사라져
        //   옛 클라이언트로 오판했다 — 순서 폴백이 되살아나 새 행이 지워질 줄을
        //   차지하고 그 줄의 계약 형태(파일럿 등)·방향·메모까지 물려받았다.
        //   "lineId 가 없으니 플래그도 빼자"고 정리하면 그 사고가 그대로 돌아온다.
        usesLineIds: true,
        // 계약 사실화 1단계 정정(26-07-28) — 편집 중인 줄의 신원을 되돌려 보낸다.
        // 새 행은 id 가 없으므로 서버가 create 로 처리한다(위 선언 덕분에 확정이다).
        lineId: r.lineId,
        modelCode: r.modelCode,
        quantity: r.quantity,
        // 계약 사실화 1단계 정정(26-07-28) — 줄이 고른 계약 형태는 **원문 7값**으로
        // 보낸다. 종전엔 접힌 2값('렌탈')을 보내 파일럿·시연·행사·사내·금융리스 줄이
        // 저장 왕복에서 렌탈로 덮였다.
        reason: r.reason || undefined,
        // 줄 칸을 '공통값 따름'으로 둔 줄만 공통 계약 형태를 실어 보낸다(화면에
        // "(공통값: 렌탈)" 로 보여 준 상속을 여기서 확정한다). 둘 다 비었으면
        // 미전송 = 기존 줄 값 보존(신규 줄은 서버가 기본값으로 기록하며 로그를 남김).
        //
        // 계약 사실화 1단계 정정5(26-07-29) — 파일럿·시연·행사·사내·금융리스만인 계약은
        // 공통 칸이 **비어 있으므로**(2값으로 그 형태를 말할 수 없다) 여기서 아무 값도
        // 실리지 않는다. 종전엔 서버가 접어 준 '렌탈'이 공통 칸에 들어와 이 줄로 흘러
        // 나갔고, 새 줄은 그 값으로 저장됐다(갈래 2). 서버에도 같은 규칙의 방어선이
        // 있지만(isFoldedEcho) 화면이 애초에 그 값을 만들지 않는 것이 뿌리를 막는 것이다.
        contractType: r.reason ? undefined : f.contractType || undefined,
        // 계약 사실화 1단계 정정(26-07-28) — 빈 칸을 **명시적으로** 보낸다('' = 지우기).
        // 종전엔 `|| undefined` 라 서버가 '무입력=유지'로 읽어, 칸을 비워 저장해도
        // 지워지지 않았다(전량 재생성 시절엔 실제로 지워졌던 동작의 회귀).
        setting: r.setting.trim(),
        grillPlate: r.grillPlate.trim(),
        specialParts: r.specialParts.trim(),
        wireGrill: r.wireGrill.trim(),
        // 금액은 **볼 수 있는 사용자만** 지운다 — 단가 섹션이 숨겨진 역할의 빈 칸이
        // 남의 금액을 0/null 로 만들지 않게(현재 저장 권한과 열람 권한이 같지만,
        // 권한이 갈리는 순간 조용히 데이터가 지워지는 종류의 사고를 막는다).
        unitPriceKrw: canSeePrice ? r.unitPriceKrw.trim() : undefined,
        listPriceKrw: canSeePrice ? r.listPriceKrw.trim() : undefined,
        // S1 Phase 5 — per-line Store ID.
        // FB2 §4B (결정 G) — 이 화면은 희망 색상/불감지를 입력 칸으로 두지 않으므로
        // 아예 보내지 않는다. 희망 방향만 모델별 picklist 값으로 전송.
        //
        // 계약 사실화 1단계 정정3(26-07-28) — 종전 주석의 "서버가 null 기록"은 이제
        // 사실이 아니다. 줄 저장이 전량 재생성에서 차이 반영(diff-update)으로 바뀌어
        // **미전송 = 기존 값 보존**이다. 그래서 이 화면에서 저장해도 희망 색상/불감지는
        // 지워지지 않고, 반대로 **어느 화면에서도 지울 수 없다**(입력 칸이 없으니까).
        //
        // 지우는 경로를 여기에 열지 않는 이유 — 다른 줄 필드(세팅·불판·특수부위·그릴·
        // Store ID·희망방향)는 화면에 **보이는 칸**이 있어서 사용자가 그 칸을 비우는
        // 행동을 '지우기'로 읽을 수 있다. 희망 색상/불감지는 이 화면에 칸도, 값 표시도
        // 없다. 여기서 ''(비움)을 보내면 사용자가 본 적도 없는 값이 저장 한 번에 사라지고,
        // 그것이 매 저장·모든 고객사에서 일어난다(제조 배정 인박스가 '색상/불감지'로
        // 읽는 값이 조용히 '미정'이 된다). 이번 공사가 닫고 있는 "안 건드렸는데 값이
        // 바뀐다"의 정확히 같은 사고를 방향만 바꿔 다시 만드는 셈이다.
        // 지우기가 필요하면 **그 값을 보여 주는 화면**(배정 인박스)에 칸을 만들어 거기서
        // 연다. 그때까지는 보존이 안전한 기본값 — 낡은 값은 눈에 보여 고칠 수 있지만
        // 지워진 값은 되돌릴 수 없다.
        storeId: r.storeId.trim(),
        desiredDirection: r.desiredDirection.trim(),
      }),
    );

    const body: SaveBody = {
      displayName,
      legalName: f.legalName.trim() || undefined,
      segment: f.segment,
      // round11 (요구 11) — 국가. 등록/편집 모두 전송(편집은 국내↔해외 정정 경로).
      countryCode,
      // round11 (요구 10) — 쇼잉 여부. 보증보험과 동일한 클리어 시멘틱 —
      // 편집 모드에서 '–' 로 되돌리면 null(미입력)로 클리어.
      isShowing: ynToBool(f.isShowing) ?? (isEdit && editing?.isShowing != null ? null : undefined),
      // S1 Phase 5 — PDF §1 '매장 수'.
      expectedStoreCount: f.expectedStoreCount.trim() ? Number(f.expectedStoreCount) : undefined,
      addressLine: f.addressLine.trim() || undefined,
      addressMapUrl: f.addressMapUrl.trim() || undefined,
      // 운영자 피드백 (round 3) — rentalCompany 만 전송. backend service 가
      // hasAJ 도 자동 derive (deriveHasAJ) 해서 back-compat 컬럼에 sync.
      rentalCompany: clearStr(f.rentalCompany, editing?.rentalCompany),
      hasInsurance:
        ynToBool(f.hasInsurance) ?? (isEdit && editing?.hasInsurance != null ? null : undefined),
      notes: clearStr(f.notes, editing?.notes),
      primaryContactName: f.primaryContactName.trim() || undefined,
      primaryContactPosition: f.primaryContactPosition.trim() || undefined,
      primaryContactPhone: f.primaryContactPhone.trim() || undefined,
      primaryContactEmail: f.primaryContactEmail.trim() || undefined,
      salesOwnerCode: f.salesOwnerCode.trim().toUpperCase(),
      contractType: f.contractType || undefined,
      contractedAt: clearDate(f.contractedAt, hadContract?.contractedAt),
      // windows-ux-edit (2026-06) — 만료일은 서버가 계약일+계약기간으로 자동 계산. endDate 미전송.
      rentalTermMonths: f.rentalTermMonths ? Number(f.rentalTermMonths) : undefined,
      depositKrw: f.depositKrw.trim() || undefined,
      // 개선 9건 차수2(26-08-04) §7 — 지원금·소개비. 비운 칸이 실제로 지워지도록
      // clearAmount 를 쓴다(보증금의 `|| undefined` 는 편집에서 '지우기'가 아예 불가능
      // 하다 — 보증금은 이 차수의 담당 범위가 아니라 동작을 바꾸지 않았고, 새로 만드는
      // 두 칸에 같은 사각을 복사하지 않는다. 보고서에 그 차이를 적어 둔다).
      subsidyKrw: clearAmount(f.subsidyKrw, hadContract?.subsidyKrw),
      referralFeeKrw: clearAmount(f.referralFeeKrw, hadContract?.referralFeeKrw),
      subscriptionStatus: clearStr(f.subscriptionStatus, hadContract?.subscriptionStatus),
      // 정정8 결함 ① — 손댄 저장만 줄을 싣는다. 미전송 = 서버가 기존 줄을 그대로 둔다.
      ...(robotsDeclaredTouched ? { robots: robotLines } : {}),
      // 정정8 결함 ① — 그 사실을 **선언**한다(편집만. 등록 DTO 에는 없는 키라 보내면 400).
      // 정정9 결함 ① — 공통 '계약 형태'를 바꿨다는 선언도 같이. 이것이 있어야 줄을 싣지
      // 않은 저장에서도 그 값이 계약 줄에 적용된다(스윕 동선). 등록은 계약 조건 자체가
      // 트리거라 이 선언이 필요 없고, 등록 DTO 에 없는 키라 보내면 400 이다.
      //
      // 차수2g 결함 ㉠ — **어느 계약을 고치는 중인지도 선언한다.** 위 계약 칸들은 전부
      // `hadContract`(= 이 창이 프리필한 계약)에서 나왔고, 아무 것도 안 바꾼 저장에서도
      // 프리필 값이 그대로 실려 나간다(보증금·지원금·소개비·계약일·구독 상태). 저장 대상은
      // 서버가 저장 시점에 다시 고르므로, 그 사이 계약이 하나 등록되면 이 값들이 **엉뚱한
      // 계약**에 박힌다. 그래서 같은 객체에서 신원을 뽑아 함께 보낸다 — 프리필의 출처와
      // 선언이 한 변수에서 나오므로 둘이 갈라질 수 없다.
      // 계약이 없던 고객사는 null 을 보낸다(= "계약이 없었다"는 선언. 그 사이 계약이 생겼다면
      // 서버가 거절한다 — 없는 줄 알고 새로 적은 금액이 남의 계약에 덮이는 것도 같은 결함이다).
      ...(isEdit
        ? {
            robotsTouched: robotsDeclaredTouched,
            contractTypeTouched,
            expectedContractId: hadContract?.id ?? null,
          }
        : {}),
      grillSelectorType: f.grillSelectorType || undefined,
      installScheduledAt: clearDate(f.installScheduledAt, hadContract?.installScheduledAt),
    };

    // ============================================================
    // 계약 사실화 1단계 정정6(26-07-29) 결함 ③ — 줄별 형태만 고른 **신규 등록**
    // 계약 사실화 1단계 정정7(26-07-29) 결함 ① — **편집에도 같은 유실 경로가 남아 있었다**
    // ============================================================
    //
    // 서버 create 는 계약일·반납일·공통 형태·기간·보증금·구독상태 중 하나라도 있을 때만
    // Contract 를 만든다(hasContractInput). 줄별 형태(robots[].reason)도, robots 배열도
    // 트리거가 아니다. 그래서 계약 조건을 비운 채 로봇 줄에서 '판매'만 골라 등록하면
    // **Contract·ContractLine 이 아예 만들어지지 않고** 그 선택이 어디에도 남지 않는다
    // (경고 로그조차 없다 — 서버의 warnIfLineReasonGuessed 는 계약을 만드는 블록 안이다).
    // 되읽기는 계약 줄에서 오므로 방금 고른 '파일럿'이 화면에서 사라진다.
    //
    // 여기서 막는 이유(서버 트리거를 늘리지 않는 이유) — 줄별 형태를 트리거로 넣으면
    // 계약 조건이 하나도 없는 고객사에도 Contract 가 생긴다. 그 계약은 만료일(계약일+기간
    // 기본 36개월)·갱신임박·구독 지표·배정 인박스가 읽는 실물이라, 아직 계약이 아닌 리드에
    // 빈 계약이 깔리는 것은 여기서 고치려는 결함보다 크다. 등록 전에 사람에게 묻는 쪽이
    // 안전하고, 되돌릴 수도 있다.
    //
    // 계약 사실화 1단계 정정7(26-07-29) 결함 ① — 정정6 은 여기서 "편집(PATCH)은 이 갈래가
    // 아니다(hasContractInput 이 robots 배열만으로도 켜진다)"며 조건을 `!isEdit` 로 좁혔다.
    // **딜이 있는 고객사에서만 맞는 말이었다.** 서버 update 의 계약 생성 갈래 조건은
    // `!계약 && (계약조건 || (딜 && hasContractInput))` 이라, 딜도 계약도 없는 **레거시
    // 고객사**(이 화면으로 만들지 않은, 딜이 없는 고객사)에서 로봇 줄만 편집한 저장은 그
    // 블록에 아예 들어오지 않고 조용히 스킵된다 — 종전의 "딜 정보가 필요합니다" 400 을
    // 없애려고 일부러 좁힌 자리다(customer-master.service 그 블록 주석).
    // 그래서 그 고객사를 편집으로 열어 로봇 줄에서 '판매'를 고르고 저장하면 **성공 토스트가
    // 뜨는데** Contract·ContractLine 이 없어 그 선택이 어디에도 남지 않고, 되읽기가 계약
    // 줄에서 오므로 화면에서도 사라진다(등록 경로와 똑같은 조용한 유실).
    //
    // ⚠ 좁힌 이유(400 회귀)를 되살리지 않는다 — 조건을 정확히 "**줄별 형태를 골랐는데
    //   담을 계약이 없다**"로 한정한다. 계약 조건도 줄별 형태도 안 건드린 평범한 저장
    //   (전화번호만 수정)은 chosenReasons 가 비어 아래 갈래에 들어오지 않는다.
    //
    // 서버 create 의 hasContractInput 과 **같은 칸**을 본다. form 값이 아니라 실제로
    // 전송되는 body 를 보므로, 전송부 규칙(clearStr·trim·|| undefined)이 바뀌어도
    // 판정이 따라 어긋나지 않는다.
    // 개선 9건 차수2(26-08-04) §7 — 서버 트리거에 지원금·소개비가 들어갔으므로 이
    // 거울도 같이 넓힌다. 어긋나면 "지원금만 넣고 줄별 형태도 고른 등록"에서 화면이
    // 없는 유실을 경고하며 저장을 막는다(서버는 계약을 만들어 잘 저장하는데 화면이
    // 거짓 안내를 한다). 두 판정은 항상 같은 칸을 봐야 한다.
    const serverWillCreateContract =
      !!body.contractedAt ||
      !!body.endDate ||
      !!body.contractType ||
      !!body.rentalTermMonths ||
      body.depositKrw != null ||
      body.subsidyKrw != null ||
      body.referralFeeKrw != null ||
      !!body.subscriptionStatus;
    // 고른 줄별 형태를 **담을 그릇**이 이번 저장에 있는가(서버 두 경로의 갈래와 같은 판정):
    //   • 편집 + 계약 있음 → 줄이 그 계약에 반영된다(syncContractLines).
    //   • 편집 + 딜 있음   → 계약 조건이 없어도 서버가 계약을 만든다(딜 && hasContractInput).
    //     계약 사실화 1단계 정정8(26-07-29) — 종전 괄호 설명("모달은 robots 를 항상 보내므로
    //     켜진다")은 이제 사실이 아니다. 로봇 줄을 손대지 않은 저장은 robots 를 보내지
    //     않으므로 그 저장은 계약을 만들지 않는다. 이 갈래로 들어오는 조건이 "줄별 형태를
    //     골랐다"(= 줄을 손댔다 → robots 를 보낸다)이므로 판정은 그대로 맞는다.
    //   • 계약 조건 동봉    → 서버가 계약을 만든다(등록·편집 공통. 편집인데 딜이 없으면
    //     서버가 명시적 400 으로 딜을 요구한다 — 조용한 유실이 아니므로 여기서 막지 않는다).
    //
    // 두 판정이 어긋날 수 있는 자리 하나 — 목록/행 조회는 **삭제된 딜을 걸러 내고**(deals
    // where deletedAt:null) 저장 경로는 걸러 내지 않는다. 그래서 "딜이 소프트 삭제된 채
    // 계약이 없는 고객사"는 화면이 딜 없음으로 보고 막는데 서버는 (삭제된 딜에) 계약을
    // 만들 수 있다. 어긋나는 방향이 **막는 쪽**이라 조용한 유실은 없고(막힌 이유도 화면에
    // 쓰인다), 삭제된 딜에 새 계약을 붙이는 그 저장은 애초에 옳지 않다. 서버 쪽 필터를
    // 맞추는 것은 별건이므로 이번 범위에 넣지 않았다.
    const lineReasonWillBeSaved =
      !!editing?.contract || !!editing?.deal || serverWillCreateContract;
    // 계약 사실화 1단계 정정8(26-07-29) — **실제로 전송되는 줄**만 본다. 로봇 줄을 손대지
    // 않은 저장은 body.robots 가 없으므로(미전송) 고른 형태도 없다 — 프리필된 값이 '운영자가
    // 고른 값'으로 오해돼 저장을 막는 일이 없다.
    const chosenReasons = [
      ...new Set((body.robots ?? []).filter((r) => r.reason).map((r) => reasonLabel(r.reason!))),
    ];
    if (chosenReasons.length > 0 && !lineReasonWillBeSaved) {
      setError(
        isEdit
          ? // 편집 + 계약도 딜도 없음. plan retire-rollback-sale-kind-2026-09 §3-2 —
            // 계약 조건을 함께 입력한 저장은 서버가 딜을 자동 생성하므로(종전 400 폐지),
            // 이제 올바른 안내는 "계약 조건을 함께 입력하라"다. 종전 "딜을 먼저 만들라"는
            // 자동 생성 도입으로 낡은 안내가 됐다.
            `로봇 줄에서 계약 형태(${chosenReasons.join(', ')})를 고르셨는데 계약 정보가 비어 있습니다. ` +
              '이대로 저장하면 계약이 만들어지지 않아 고른 형태가 저장되지 않습니다. ' +
              '계약일 등 계약 정보를 함께 입력하면 영업(딜) 카드가 자동 생성되며 저장됩니다. 아니면 로봇 줄의 형태를 "공통값 따름"으로 되돌려 주세요.'
          : `로봇 줄에서 계약 형태(${chosenReasons.join(', ')})를 고르셨는데 계약 정보가 비어 있습니다. ` +
              '이대로 등록하면 계약이 만들어지지 않아 고른 형태가 저장되지 않습니다. ' +
              // 개선 9건 차수2(26-08-04) §7 — 트리거가 늘었으니 안내 문구도 같이 늘린다
              // (안내가 실제 트리거보다 좁으면 "적으라는 걸 다 적었는데 안 된다"가 된다).
              '계약일, 계약 기간, 보증금, 지원금, 소개비, 구독 상태 중 하나를 입력하거나, 로봇 줄의 형태를 "공통값 따름"으로 되돌려 주세요.',
      );
      return;
    }

    // 2026-09-30 오너 보고("입력한 Store ID 가 상세 화면에 안 보인다") — 줄별 **사양**도
    // 같은 그릇(계약 줄)에만 담긴다. 계약이 만들어지지 않는 저장이면 서버는 모델·대수만
    // 딜 견적으로 남기고 세팅·불판·특수부위·그릴·Store ID·구분 사양·줄별 금액을 **경고 없이** 버린다
    // (customer-master.service create 의 무계약 갈래). 위 형태 차단과 같은 판정·같은 안내로
    // 막는다 — 형태만 막고 사양은 흘려보내면 "적었는데 어디에도 없다"가 그대로 남는다.
    //
    // 실제로 전송되는 줄의 **공백 아닌** 칸만 본다(전송부가 trim 한 값). 줄을 손대지 않은
    // 편집은 robots 를 보내지 않으므로 여기 들어오지 않고, 계약 줄에서 프리필된 값은
    // 계약이 있을 때만 생기므로(lineReasonWillBeSaved = true) 막히지 않는다.
    //
    // 방향 칸은 **축이 있는 모델에만** 보인다(모델 줄의 EditableSelect). 축 없는 모델의 줄에
    // 방향 값이 남아 있으면 사람이 보지도 지우지도 못하므로 그 값으로는 막지 않는다
    // (모델을 바꿀 때 updateRobot 이 비우지만, 모델 목록이 늦게 온 경우 등의 방어선).
    // 안내의 칸 이름도 화면의 그 칸 이름(모델의 축 이름)을 쓴다.
    const typedSpecLabels = [
      ...new Set(
        (body.robots ?? []).flatMap((r) => {
          const model = modelItems.find((m) => m.code === r.modelCode);
          return LINE_SPEC_LABELS.flatMap(([k, label]) => {
            if (k === 'desiredDirection') {
              if (!model?.variantOptionGroupKey || !lineFieldHasValue(r[k], false)) return [];
              return [model.variantAxisLabel ?? label];
            }
            return lineFieldHasValue(r[k], k === 'unitPriceKrw' || k === 'listPriceKrw')
              ? [label]
              : [];
          });
        }),
      ),
    ];
    if (typedSpecLabels.length > 0 && !lineReasonWillBeSaved) {
      const what = `로봇 줄에 적은 값(${typedSpecLabels.join(', ')})은 계약에 담겨 저장되는데, 계약 정보가 비어 있습니다. `;
      setError(
        isEdit
          ? what +
              '이대로 저장하면 계약이 만들어지지 않아 그 값이 저장되지 않습니다. ' +
              '계약일 등 계약 정보를 함께 입력하면 영업(딜) 카드가 자동 생성되며 저장됩니다. 아니면 그 칸을 비워 주세요.'
          : what +
              '이대로 등록하면 계약이 만들어지지 않아 그 값이 저장되지 않습니다. ' +
              '계약일, 계약 기간, 보증금, 지원금, 소개비, 구독 상태 중 하나를 입력하거나, 그 칸을 비워 주세요.',
      );
      return;
    }

    // 2026-09-30 — '희망 설치 일정'도 계약에 딸린 그릇(설치 준비 기록 = InfrastructureOrder,
    // 계약 1건당 1개)에만 담긴다. 계약이 생기지 않는 저장이면 서버는 날짜를 둘 곳이 없어
    // 성공 응답을 주고 **날짜만 버렸다**(2026-05-22 부터 — 서버 create/update 의 설치 준비
    // 기록 블록이 계약이 있을 때만 돈다). 위 두 차단과 같은 원칙으로 저장 전에 막는다.
    //
    // ⚠ 그릇 판정이 위 lineReasonWillBeSaved 와 **다르다.** 거기서 "딜이 있으면 통과"인 근거는
    //   "줄을 고른 저장은 줄을 싣고, 서버는 딜 + 줄이면 계약을 만든다"이다. 날짜는 줄과 무관하게
    //   적히므로 딜이 있어도 **줄을 실은 저장**일 때만 계약이 생긴다(서버 update 의
    //   `딜 && hasContractInput`). 그 판정을 빌려 쓰면 딜만 있는 고객사에서 날짜가 다시 조용히
    //   사라진다.
    // ⚠ '가스 타입'(grillSelectorType)은 넣지 않는다 — 고객사 자체에도 저장되고 화면도 거기서
    //   먼저 읽으므로(행의 grillSelector.type) 계약이 없어도 사라지지 않는다.
    //
    // 서버 트리거를 늘리지 않는 이유는 위 줄별 형태 차단과 같다(빈 DRAFT 계약이 만료·갱신·구독
    // 지표와 배정 인박스를 오염시킨다). 계약 없이 날짜를 남기려면 비고에 적도록 안내한다.
    const installOrderWillBeSaved =
      !!editing?.contract ||
      serverWillCreateContract ||
      (!!editing?.deal && (body.robots?.length ?? 0) > 0);
    if (body.installScheduledAt && !installOrderWillBeSaved) {
      const what = "'희망 설치 일정'은 계약에 담겨 저장되는데, 계약 정보가 비어 있습니다. ";
      const keep = " 날짜를 남겨 두려면 '비고'에 적어 두세요.";
      setError(
        isEdit
          ? what +
              '이대로 저장하면 계약이 만들어지지 않아 희망 설치 일정이 저장되지 않습니다. ' +
              '계약일 등 계약 정보를 함께 입력하거나, 희망 설치 일정을 비워 주세요.' +
              keep
          : what +
              '이대로 등록하면 계약이 만들어지지 않아 희망 설치 일정이 저장되지 않습니다. ' +
              '계약일, 계약 기간, 보증금, 지원금, 소개비, 구독 상태 중 하나를 입력하거나, 희망 설치 일정을 비워 주세요.' +
              keep,
      );
      return;
    }

    saveMutation.mutate(body);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? '고객사 마스터 편집' : '신규 고객사 마스터 등록'}
      width={760}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="customer-master-create-form"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? '저장 중…' : isEdit ? '저장' : '등록'}
          </Button>
        </>
      }
    >
      <form id="customer-master-create-form" onSubmit={submit} noValidate>
        <div ref={errorRef}>
          <FormError>{error}</FormError>
        </div>

        {/* ① 고객사 정보 — PDF §1 (3-col). S1 Phase 5 (R2 #3). */}
        <SectionTitle>고객사 정보</SectionTitle>
        <Grid3>
          <Field label="상호 (브랜드명)" htmlFor="cm-name" required>
            <Input id="cm-name" value={f.displayName} onChange={set('displayName')} required />
          </Field>
          <Field label="법인명 (선택)" htmlFor="cm-legal">
            <Input id="cm-legal" value={f.legalName} onChange={set('legalName')} />
          </Field>
          <Field label="고객 유형" htmlFor="cm-segment">
            <EditableSelect
              id="cm-segment"
              groupKey="customer.segment"
              value={f.segment}
              onChange={(v) => setF((c) => ({ ...c, segment: v }))}
              placeholder="— 선택 —"
            />
          </Field>
        </Grid3>
        {/* round11 (요구 11 / 요구 10) — 국가 · 쇼잉 여부 · 매장 수. */}
        <Grid3>
          <Field
            label="국가"
            htmlFor="cm-country"
            hint="2자리 코드로 적습니다. 예: KR, US, JP"
            required
          >
            <Input
              id="cm-country"
              value={f.countryCode}
              onChange={(e) =>
                setF((c) => ({ ...c, countryCode: e.target.value.toUpperCase().slice(0, 2) }))
              }
              placeholder="KR"
              required
            />
          </Field>
          <YN
            label="쇼잉 여부"
            id="cm-showing"
            value={f.isShowing}
            onChange={(v) => setF((c) => ({ ...c, isShowing: v }))}
          />
          <Field label="매장 수" htmlFor="cm-store-count">
            <Input
              id="cm-store-count"
              type="number"
              min={0}
              inputMode="numeric"
              value={f.expectedStoreCount}
              onChange={set('expectedStoreCount')}
              placeholder="예: 3"
            />
          </Field>
        </Grid3>
        <Grid2>
          <Field label="위치" htmlFor="cm-addr">
            <Input
              id="cm-addr"
              value={f.addressLine}
              onChange={set('addressLine')}
              placeholder="서울시 강남구 ..."
            />
          </Field>
          <Field label="지도 URL (네이버/구글)" htmlFor="cm-map">
            <Input
              id="cm-map"
              value={f.addressMapUrl}
              onChange={set('addressMapUrl')}
              placeholder="https://map.naver.com/..."
            />
          </Field>
        </Grid2>
        <Field label="비고" htmlFor="cm-notes">
          <Textarea id="cm-notes" rows={2} value={f.notes} onChange={set('notes')} />
        </Field>

        {/* ② 고객 담당자 — PDF §2 (2-col). */}
        <SectionTitle>고객 담당자</SectionTitle>
        <Grid2>
          <Field label="담당자명" htmlFor="cm-cn">
            <Input
              id="cm-cn"
              value={f.primaryContactName}
              onChange={set('primaryContactName')}
              placeholder="김사장"
            />
          </Field>
          <Field label="직함" htmlFor="cm-cp">
            <Input
              id="cm-cp"
              value={f.primaryContactPosition}
              onChange={set('primaryContactPosition')}
              placeholder="대표 / 점주"
            />
          </Field>
        </Grid2>
        <Grid2>
          <Field label="전화번호" htmlFor="cm-cph">
            <Input
              id="cm-cph"
              type="tel"
              inputMode="tel"
              value={f.primaryContactPhone}
              // round10 B5 — 입력 시 자동 하이픈(길이/prefix 분기).
              onChange={(e) =>
                setF((c) => ({ ...c, primaryContactPhone: formatKoreanPhone(e.target.value) }))
              }
              placeholder="010-1234-5678"
            />
          </Field>
          <Field label="이메일" htmlFor="cm-ce">
            <Input
              id="cm-ce"
              type="email"
              value={f.primaryContactEmail}
              onChange={set('primaryContactEmail')}
              placeholder="kim@company.com"
            />
          </Field>
        </Grid2>

        {/* ③ 도입 로봇 모델 — PDF §3. row extras 에 per-line Store ID 추가.
            운영자 피드백 (round 3):
            ① SectionTitle ~ 로봇 row ~ "+ 로봇 모델 추가" 사이 간격을 좁혀
               (gap 8 → 4) 하나의 시각적 group 으로 묶음.
            ② "+ 로봇 모델 추가" ~ extras (특수부위/그릴/Store ID) 사이는
               별도 group 이므로 marginTop: 16 로 분리. */}
        <SectionTitle>도입 로봇 모델</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {f.robots.map((r, idx) => {
            // FB2 §4B — 선택 모델의 '구분 사양'(variant) 축으로 희망 방향
            // picklist 를 구동. 축이 없는 모델(SEN/HEADLESS 등,
            // variantOptionGroupKey == null)은 방향 입력을 숨긴다.
            const selectedModel = modelItems.find((m) => m.code === r.modelCode);
            return (
              <div key={idx} style={robotRowStyle}>
                <div style={{ flex: '1 1 110px' }}>
                  {/* windows-ux-edit (2026-06) — 모델 소스 단일화: 기존 MODEL_CODES
                      상수 <Select> 대신 정전 소스 Model 레지스트리(/models)를 쓰는
                      공용 ModelPicker 로 통일(RobotForm 과 동일 소스). */}
                  <ModelPicker
                    id={`cm-model-${idx}`}
                    emit="code"
                    value={r.modelCode}
                    onChange={(v) => updateRobot(idx, 'modelCode', v)}
                  />
                </div>
                {/* FB2 §4B — 희망 방향(모델별 picklist). 자유텍스트 Input 에서
                    모델의 variantOptionGroupKey 기반 EditableSelect 로 전환하고
                    extras 그룹이 아닌 모델 다중선택부로 이동. 라벨은 모델의
                    variantAxisLabel(없으면 '희망 방향'). */}
                {selectedModel?.variantOptionGroupKey && (
                  <div style={{ flex: '1 1 110px' }}>
                    <EditableSelect
                      id={`cm-direction-${idx}`}
                      groupKey={selectedModel.variantOptionGroupKey}
                      value={r.desiredDirection}
                      onChange={(v) => updateRobot(idx, 'desiredDirection', v)}
                      placeholder={selectedModel.variantAxisLabel ?? '희망 방향'}
                    />
                  </div>
                )}
                <div style={{ flex: '0 0 60px' }}>
                  <Input
                    type="number"
                    min={1}
                    value={r.quantity}
                    onChange={(e) => updateRobot(idx, 'quantity', e.target.value)}
                  />
                </div>
                <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>대</span>
                <div style={{ flex: '1 1 110px' }}>
                  <EditableSelect
                    groupKey="contract.robotSetting"
                    value={r.setting}
                    onChange={(v) => updateRobot(idx, 'setting', v)}
                    placeholder="세팅"
                  />
                </div>
                <div style={{ flex: '1 1 110px' }}>
                  <EditableSelect
                    groupKey="contract.grillPlate"
                    value={r.grillPlate}
                    onChange={(v) => updateRobot(idx, 'grillPlate', v)}
                    placeholder="불판"
                  />
                </div>
                {/* 계약 사실화 1단계(26-07-28, 결정 ②A) — 줄별 렌탈/판매.
                    "GEN2 1대는 판매, ZEPHYR 2대는 렌탈" 이 실재하는데 지금까지
                    계약 형태가 고객사당 하나뿐이라 입력할 방법이 없었다.
                    비우면 아래 공통 '계약 형태' 를 상속하고, 상속 중이라는 사실을
                    옆에 회색으로 적는다(조용한 기본값 금지).
                    계약 사실화 1단계 정정(26-07-28) — 선택지를 계약의 7값 전부로
                    넓혔다(파일럿·시연·행사·사내·금융리스). 종전엔 2값만 있어 그
                    밖의 줄이 '렌탈'로 보이고 저장하면 실제로 렌탈이 됐다.
                    계약 사실화 1단계 정정4(26-07-29) — 문구가 표기 정본의 긴 표기
                    ('구독(운용리스)')로 바뀌면서 104px 로는 닫힌 상태의 글자가
                    잘렸다(13px 글자 8자 + 좌우 여백 + 화살표). 140px 로 넓힌다 —
                    종전에도 '공통값 따름'이 빠듯했다. 줄은 flexWrap 이라 좁은
                    창에서는 다음 행으로 접힌다. */}
                <div style={{ flex: '0 0 140px' }}>
                  <Select
                    id={`cm-line-ctype-${idx}`}
                    aria-label={`#${idx + 1} 로봇 계약 형태`}
                    value={r.reason}
                    onChange={(e) => updateRobot(idx, 'reason', e.target.value)}
                  >
                    <option value="">공통값 따름</option>
                    {LINE_REASON_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {/* 계약 사실화 1단계 정정5(26-07-29) — "이 줄이 무엇으로 저장될지"를
                    적는다. 종전엔 공통값이 있으면 "(공통값: 렌탈)", 없으면 "(공통값
                    없음)"이었다. 둘 다 불충분했다 —
                    ① 파일럿 계약에서 "(공통값: 렌탈)"이라 약속하고 서버는 파일럿을
                       유지했다(화면이 거짓을 말했다),
                    ② 혼합 계약에 새 줄을 추가하면 "(공통값 없음)"만 떠서, 그 줄이
                       무엇으로 저장되는지(구독) 알 방법이 없었다.
                    이제 문구는 서버와 같은 규칙(planLineType)이 만들고, 값 이름은
                    표기 정본에서 가져온다. */}
                {!r.reason && (
                  <span
                    data-testid={`cm-line-ctype-inherit-${idx}`}
                    style={{ fontSize: 11, color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}
                  >
                    {lineTypeNote(linePlans[idx]!)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeRobotRow(idx)}
                  disabled={f.robots.length <= 1}
                  style={iconBtn}
                  title="이 행 삭제"
                  aria-label="이 행 삭제"
                >
                  삭제
                </button>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button type="button" variant="ghost" onClick={addRobotRow}>
              <Icon name="plus" size={16} /> 로봇 모델 추가
            </Button>
            <div style={{ fontSize: 13 }}>
              합산 수량 <strong>{totalQty}</strong>대
            </div>
          </div>
          {/* FB §5 — 중복 모델 비차단 안내. 서버가 견적에서 모델당 1행으로
              수량을 합산하므로 제출은 막지 않는다. */}
          {hasDuplicateModels && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg-muted)',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 6,
                padding: '6px 10px',
                marginTop: 4,
              }}
            >
              같은 모델은 견적에서 수량을 합칩니다. 도입 로봇 줄은 입력한 그대로 남습니다.
            </div>
          )}
          {/* 개선 9건 차수2c(26-08-04) §5 — 새 계약 유도. 막지 않고 안내하며, 그 자리에서
              이동할 수 있게 한다. 문구는 서버 가드(409)와 **같은 문장**이고, 차수2d 부터
              같은 출처(@bhops/shared 의 INSTALLED_CONTRACT_LINE_ADD_MESSAGE)에서 가져온다 —
              종전엔 같은 문장을 여기 리터럴로 다시 적어, 서버만 고치면 이 안내가 낡았다. */}
          {addsToAssignedContract && (
            <div
              data-testid="cm-new-contract-guidance"
              style={addsRobotsNotice(addBlockedByServer)}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {addBlockedByServer
                  ? INSTALLED_CONTRACT_LINE_ADD_MESSAGE
                  : '이 계약은 배정이 진행 중입니다. 추가 판매라면 새 계약으로 등록해 주세요.'}
              </div>
              <div style={{ marginBottom: 6 }}>
                {editingPrimaryContract?.humanId ?? '이 계약'}에 로봇을 더 붙이는 변경입니다.
                {addBlockedByServer
                  ? ' 이대로는 저장할 수 없습니다. 배정이 끝난 계약에 붙인 로봇은 어느 화면에도 나오지 않기 때문입니다.'
                  : ' 저장은 되지만, 조건이 다른 판매는 계약을 나누어 등록하는 것이 회사 규칙입니다.'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 6 }}>
                새 계약에는 영업(딜)이 하나 필요합니다. 딜이 없는 고객사는 저장할 때 자동으로
                만들어지니, 영업 담당을 꼭 입력해 주세요.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    onClose();
                    navigate(`/contracts/new?customerId=${editing?.id ?? ''}`);
                  }}
                >
                  새 계약으로 등록
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    onClose();
                    navigate('/business/deals');
                  }}
                >
                  영업(딜) 화면 열기
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* 운영자 피드백 (round 3) — "+ 로봇 모델 추가" 와 본 extras group 을
            시각적으로 분리. PDF §3: 특수부위 / 그릴 종류 / Store ID (3-col).
            FB2 §4B — 희망 색상/불감지 제거(결정 G), 희망 방향은 위 모델
            다중선택부의 모델별 picklist 로 이동. */}
        <div style={{ marginTop: 16 }}>
          {f.robots.map((r, idx) => (
            <div key={`extra-${idx}`}>
              <Grid3>
                <Field label={`#${idx + 1} 로봇 특수부위`} htmlFor={`cm-sp-${idx}`}>
                  <Input
                    id={`cm-sp-${idx}`}
                    value={r.specialParts}
                    onChange={(e) => updateRobot(idx, 'specialParts', e.target.value)}
                    placeholder="예: 항정, 가브리"
                  />
                </Field>
                <Field label={`#${idx + 1} 로봇 그릴 종류`} htmlFor={`cm-wg-${idx}`}>
                  <Input
                    id={`cm-wg-${idx}`}
                    value={r.wireGrill}
                    onChange={(e) => updateRobot(idx, 'wireGrill', e.target.value)}
                    placeholder="예: 사선 그릴"
                  />
                </Field>
                <Field label={`#${idx + 1} 로봇 Store ID`} htmlFor={`cm-store-${idx}`}>
                  <Input
                    id={`cm-store-${idx}`}
                    value={r.storeId}
                    onChange={(e) => updateRobot(idx, 'storeId', e.target.value)}
                    placeholder="예: STORE-001"
                  />
                </Field>
              </Grid3>
            </div>
          ))}
        </div>

        {/* ④ 계약 정보 — PDF §4. 영업 담당 / 렌탈사 / 보증보험 통합. */}
        <SectionTitle>계약 정보</SectionTitle>
        {/* 개선 9건 차수2c(26-08-04) §5 — 다계약 고지.
            이 창은 계약 **한 건**(가장 최근 계약)을 고친다. 시트의 반납일·D-day 는 가장
            임박한 만기 계약을 보므로 두 값이 다를 수 있다 — 그 사실을 말하지 않으면
            "시트엔 2026년인데 창엔 2029년"이 되어 운영자가 잘못된 계약을 고친다.

            개선 9건 차수2g(26-08-04) 결함 ㉡ — **금액도 같은 고지가 필요하다.** 종전 이 블록은
            반납일·D-day 만 적어서, 시트 보증금 ₩1,500,000 과 이 창의 빈 보증금 칸을 나란히 본
            운영자가 '누락'으로 읽고 그 금액을 옮겨 적었다(없던 보증금이 이 계약에 생기고 시트
            합이 ₩3,000,000 이 된다).

            **금액 칸 옆 도움말이 아니라 이 블록을 넓힌 이유**(판단 근거):
              ① 같은 사실 하나다 — "이 창은 계약 한 건을 고치고, 그 계약의 값만 보여 준다".
                 날짜 절과 금액 절을 다른 자리에 두면 한쪽만 고쳐져 두 문장이 갈라진다.
              ② 이 블록은 '계약 정보' 머리라 금액 3칸(보증금·지원금·소개비)과 아래 로봇별
                 단가보다 **위**에 있다 — 칸을 채우기 전에 읽힌다.
              ③ 도움말로 두면 같은 문장을 3칸(+단가 줄마다)에 반복해야 하고, 그 hint 슬롯은
                 이미 다른 사실("계약서에는 인쇄되지 않습니다")이 쓰고 있다.
            계약이 1건인 고객사에서는 이 블록이 아예 뜨지 않는다 — 그때는 시트 금액과 창의
            금액이 같은 계약을 말하므로 고지할 사실이 없다(노이즈 금지). */}
        {showMultiContractNotice && (
          <div data-testid="cm-multi-contract-notice" style={multiContractNoticeStyle}>
            이 고객사에는 계약이 {editingContracts.length}건 있습니다. 이 창은 가장 최근에 등록한{' '}
            <strong>{editingPrimaryContract?.humanId ?? '계약'}</strong> 하나만 고칩니다
            {soonestOfEditing && !soonestOfEditing.isPrimary ? (
              <>
                . 목록의 반납일과 D-day는 만기가 먼저 오는{' '}
                <strong>{soonestOfEditing.humanId}</strong> 기준입니다. 다른 계약은 계약 상세에서
                고쳐 주세요.
              </>
            ) : (
              '.'
            )}
            {/* 금액 절은 가격을 볼 수 있는 역할에게만 — 잠긴 칸(입력 불가)에 "옮겨 적지
                마세요"라고 말하면 할 수 없는 행동을 안내하는 것이 된다. */}
            {canSeePrice && (
              <div data-testid="cm-multi-contract-amount-notice" style={{ marginTop: 6 }}>
                아래 보증금, 지원금, 소개비와 로봇별 단가는{' '}
                <strong>{editingPrimaryContract?.humanId ?? '이'} 계약 하나의 값</strong>
                입니다. 빈 칸은 이 계약에 그 금액이 없다는 뜻입니다. {sheetAmountBasisText}.
                목록에서 본 금액을 이 칸에 옮겨 적으면 없던 금액이 이 계약에 새로 생기고 목록의
                합계도 그만큼 늘어납니다. 다른 계약의 금액은 계약 상세에서 확인하고 고쳐 주세요.
              </div>
            )}
          </div>
        )}
        {/* 이슈1b — 계약정보 6필드를 2행 3열로 재배치.
            ROW A = 영업 담당 · 렌탈사 · 보증보험. */}
        <Grid3>
          <Field label="영업 담당" htmlFor="cm-sales" required>
            <EditableSelect
              id="cm-sales"
              groupKey="customer.salesOwnerCode"
              value={f.salesOwnerCode}
              onChange={(v) => setF((c) => ({ ...c, salesOwnerCode: v }))}
              placeholder="— 선택 —"
            />
          </Field>
          {/* 운영자 피드백 (round 3) — '렌탈사' EditableSelect(customer.rentalCompany).
              매니저는 인라인 추가/보관/복원 가능. backend service.upsert 가 hasAJ 도 sync. */}
          <Field label="렌탈사" htmlFor="cm-rental-company">
            <EditableSelect
              id="cm-rental-company"
              groupKey="customer.rentalCompany"
              value={f.rentalCompany}
              onChange={(v) => setF((c) => ({ ...c, rentalCompany: v }))}
              placeholder="– 선택 –"
            />
          </Field>
          <YN
            label="보증보험"
            value={f.hasInsurance}
            onChange={(v) => setF((c) => ({ ...c, hasInsurance: v }))}
          />
        </Grid3>
        {/* 이슈1b — ROW B = 계약 형태 · 계약일 · 계약 기간(개월).
            (만료일 입력은 슬라이드3 에서 제거 — 서버가 계약일+기간으로 자동 계산.
            '계약 번호'도 round5 #2b① 에서 제거 — humanId 자동 발번.) */}
        <Grid3>
          {/* 계약 사실화 1단계(26-07-28) — 이 칸은 이제 '공통값'이다. 줄별 칸이 빈
              줄에만 적용되며, 줄별로 갈린 고객사에서는 그 사실을 hint 로 말한다
              (혼합 표기 '판매 1 · 렌탈 2' 는 <Select> 값이 될 수 없어 칸은 빈다). */}
          <Field label="계약 형태" htmlFor="cm-ctype" hint={contractTypeHint}>
            {/* 계약 사실화 1단계 정정9(26-07-29) 결함 ① — 이 칸을 바꾼 저장은 그 사실을
                선언한다(setContractType). 종전엔 set('contractType') 이라 값만 실려 나갔고,
                줄을 손대지 않은 저장에서는 그 값이 계약 줄에 적용되지 않았다. */}
            <Select id="cm-ctype" value={f.contractType} onChange={setContractType}>
              <option value="">선택 안 함</option>
              <option value="렌탈">렌탈</option>
              <option value="판매">판매</option>
            </Select>
          </Field>
          <Field label="계약일" htmlFor="cm-cdate">
            <Input
              id="cm-cdate"
              type="date"
              value={f.contractedAt}
              onChange={set('contractedAt')}
            />
          </Field>
          <Field
            label="계약 기간 (개월)"
            htmlFor="cm-cterm"
            hint="만료일은 자동으로 계산됩니다. 예: 12, 24, 36"
          >
            <Input
              id="cm-cterm"
              type="number"
              min={1}
              value={f.rentalTermMonths}
              onChange={set('rentalTermMonths')}
            />
          </Field>
        </Grid3>
        {/* 개선 9건 차수2(26-08-04) §7 (결정 ④A) — 보증금 **옆에** 지원금·소개비.
            세 칸이 같은 성격(계약 단위 금액)이라 한 줄(Grid3)로 묶고, 구독 상태는
            성격이 달라 아래 줄로 내렸다. 가격 권한 게이팅도 보증금과 같은 모양
            (라벨 '(잠금)' + disabled) — 권한 없는 사용자는 값을 보지도 고치지도 못한다.
            ⚠ 이 두 금액은 계약서 PDF 에 인쇄되지 않는다(결정 ⑤A — 내부 지출 정보). */}
        <Grid3>
          <Field label={canSeePrice ? '보증금 (KRW)' : '보증금 (잠금)'} htmlFor="cm-deposit">
            <Input
              id="cm-deposit"
              type="number"
              inputMode="numeric"
              value={f.depositKrw}
              onChange={set('depositKrw')}
              disabled={!canSeePrice}
              placeholder="예: 1500000"
            />
          </Field>
          <Field
            label={canSeePrice ? '지원금 (KRW)' : '지원금 (잠금)'}
            htmlFor="cm-subsidy"
            hint="계약서에는 인쇄되지 않습니다"
          >
            <Input
              id="cm-subsidy"
              type="number"
              inputMode="numeric"
              value={f.subsidyKrw}
              onChange={set('subsidyKrw')}
              disabled={!canSeePrice}
              placeholder="예: 1000000"
            />
          </Field>
          <Field
            label={canSeePrice ? '소개비 (KRW)' : '소개비 (잠금)'}
            htmlFor="cm-referral"
            hint="계약서에는 인쇄되지 않습니다"
          >
            <Input
              id="cm-referral"
              type="number"
              inputMode="numeric"
              value={f.referralFeeKrw}
              onChange={set('referralFeeKrw')}
              disabled={!canSeePrice}
              placeholder="예: 500000"
            />
          </Field>
        </Grid3>
        <Grid2>
          <Field label="구독 상태" htmlFor="cm-sub">
            <Select id="cm-sub" value={f.subscriptionStatus} onChange={set('subscriptionStatus')}>
              <option value="">선택 안 함</option>
              <option value="정상">정상</option>
              <option value="휴면">휴면</option>
              <option value="해지">해지</option>
            </Select>
          </Field>
        </Grid2>

        {/* ⑤ 로봇별 단가 — PDF §5. canSeePrice 일 때만. */}
        {canSeePrice && (
          <>
            <SectionTitle>로봇별 단가</SectionTitle>
            {f.robots.map((r, idx) => (
              <Grid2 key={`price-${idx}`}>
                <Field label={`#${idx + 1} 구독료 (대당, KRW)`} htmlFor={`cm-up-${idx}`}>
                  <Input
                    id={`cm-up-${idx}`}
                    type="number"
                    inputMode="numeric"
                    value={r.unitPriceKrw}
                    onChange={(e) => updateRobot(idx, 'unitPriceKrw', e.target.value)}
                    placeholder="예: 680000"
                  />
                </Field>
                <Field label={`#${idx + 1} 판매가 (대당, KRW)`} htmlFor={`cm-lp-${idx}`}>
                  <Input
                    id={`cm-lp-${idx}`}
                    type="number"
                    inputMode="numeric"
                    value={r.listPriceKrw}
                    onChange={(e) => updateRobot(idx, 'listPriceKrw', e.target.value)}
                    placeholder="예: 1400000"
                  />
                </Field>
              </Grid2>
            ))}
          </>
        )}

        {/* ⑥ 기타 정보 — PDF §6. 가스 타입 + 희망 설치 일정 (2-col).
            S1 Phase 4: '간택기 설치 상태' 제거 (사업팀 책임 외). */}
        <SectionTitle>기타 정보</SectionTitle>
        <Grid2>
          <Field label="가스 타입" htmlFor="cm-gt">
            <EditableSelect
              id="cm-gt"
              groupKey="customer.grillSelectorType"
              value={f.grillSelectorType}
              onChange={(v) => setF((c) => ({ ...c, grillSelectorType: v }))}
              placeholder="— 선택 —"
            />
          </Field>
          <Field label="희망 설치 일정" htmlFor="cm-installAt">
            <Input
              id="cm-installAt"
              type="date"
              value={f.installScheduledAt}
              onChange={set('installScheduledAt')}
            />
          </Field>
        </Grid2>

        {/* §9 첨부 — S1 Phase 4: 섹션 전체 제거. 첨부는 S5a 필드팀 PWA 에서 재도입. */}
      </form>
    </Modal>
  );
}

function YN({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id?: string;
  value: 'O' | 'X' | '';
  onChange: (v: 'O' | 'X' | '') => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value as 'O' | 'X' | '')}>
        <option value="">–</option>
        <option value="O">O (예)</option>
        <option value="X">X (아니오)</option>
      </Select>
    </Field>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: '16px 0 6px',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--fg-default)',
        borderBottom: '1px solid var(--border-subtle)',
        paddingBottom: 4,
      }}
    >
      {children}
    </h3>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

// S1 Phase 5 (R2 #3) — PDF §1 / §3 / §4 의 3-col row.
function Grid3({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {children}
    </div>
  );
}

const robotRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
// 개선 9건 차수2c(26-08-04) §5 — 새 계약 유도 안내. 서버가 거부하는 경우(설치 완료)는
// 경고 톤, 받아 주는 경우(설치 대기)는 중립 톤 — 톤이 결과를 예고해야 한다.
function addsRobotsNotice(blocked: boolean): CSSProperties {
  return {
    fontSize: 12,
    marginTop: 8,
    padding: '8px 10px',
    borderRadius: 6,
    background: blocked ? 'var(--bg-warning-subtle)' : 'var(--bg-subtle)',
    border: `1px solid ${blocked ? 'var(--border-warning-saturated)' : 'var(--border-subtle)'}`,
    color: 'var(--fg-default)',
  };
}
// 개선 9건 차수2c(26-08-04) §5 — 다계약 고지(계약 정보 머리).
const multiContractNoticeStyle: CSSProperties = {
  fontSize: 12,
  marginBottom: 8,
  padding: '8px 10px',
  borderRadius: 6,
  background: 'var(--bg-info-subtle)',
  border: '1px solid var(--border-subtle)',
  color: 'var(--fg-default)',
};
const iconBtn: CSSProperties = {
  border: '1px solid var(--border-default)',
  background: 'transparent',
  borderRadius: 'var(--r-btn)',
  padding: '4px 8px',
  cursor: 'pointer',
};
